import type { MessageTree } from "../types";

/**
 * 简体中文文案目录（前端唯一真相源）。
 *
 * 约定：
 * - 只收录前端自有文案；项目名、问题正文、测试集内容、提示词、原始日志、AI Engineer/Judge 输出与 ID 不进目录。
 * - 品牌名与技术缩写（Agent Judge、Judge、AI Engineer、API、LLM、Production 等）保持原样。
 * - 变量插值统一用 `{name}`，两种语言必须使用相同的变量名。
 */
export const zhCN = {
  qaeCollection: {"startInstructions": "以租户管理员身份打开 QAE，找到已完成的普通问答，点回答下方的绿色「同步测试平台」按钮。回到本页后，核对问题、答案和工具记录，再确认保存。", "openSource": "打开 QAE（{host}）", "backToBank": "返回项目题库", "unavailable": "当前账号或项目未启用问题收集。", "sourcesFailed": "暂时无法读取 QAE 来源，请刷新重试。", "title": "问题收集", "description": "核对从 QAE 带入的完整问答和记录，确认后保存到当前项目。", "expired": "问题收集链接缺失或已过期，请回到 QAE 重新点击绿色图标。", "saved": "问题已保存", "savedDescription": "完整问题、答案和工具记录已保存，并生成了新的测试集。", "viewQuestion": "查看已保存的问题", "loading": "正在读取完整问答和工具记录…", "context": "选择复现问题所需的前文", "round": "第 {round} 轮", "contextRound": "前文第 {round} 轮", "target": "目标项目", "question": "本次问题", "answer": "完整标准答案", "files": "附件与文件引用", "baseSuite": "沿用哪个测试集的问题", "noBaseSuite": "不沿用，只使用本次问答", "suiteName": "新测试集名称", "reviewed": "我已核对目标项目、问题、完整答案和工具记录", "saving": "正在保存…", "save": "确认保存到题库", "tenant": "来源租户", "tools": "完整工具与来源记录"},
  qaeSync: { syncedSteps: "QAE 原始工具步骤", evidence: "同步来源记录" },
  language: {
    label: "语言",
    switchAria: "切换界面语言",
    "zh-CN": "简体中文",
    "en-US": "English",
    "de-DE": "Deutsch",
  },
  common: {
    save: "保存",
    saving: "保存中…",
    cancel: "取消",
    confirm: "确认",
    close: "关闭",
    edit: "编辑",
    delete: "删除",
    remove: "移除",
    create: "新建",
    refresh: "刷新",
    refreshing: "刷新中…",
    retryLater: "请稍后重试。",
    requestFailedRetry: "请求失败，请稍后重试",
    project: "项目",
    suite: "测试集",
    type: "类型",
    duration: "耗时",
    status: "状态",
    createdAt: "创建时间",
    updatedTime: "更新时间",
    updatedAt: "已更新于 {time}",
    sortCreatedDesc: "创建时间倒序",
    reload: "重新加载",
    actions: "操作",
    retry: "重试",
    search: "搜索",
    filter: "筛选",
    sort: "排序",
    reset: "重置",
    back: "返回",
    copy: "复制",
    copied: "已复制",
    copyFailed: "复制失败",
    loading: "加载中…",
    empty: "暂无数据",
    unknown: "未知",
    none: "无",
    all: "全部",
    yes: "是",
    no: "否",
    enabled: "启用",
    disabled: "停用",
    optional: "选填",
    required: "必填",
    detail: "详情",
    more: "更多",
    apply: "应用",
    submit: "提交",
    export: "导出",
    import: "导入",
    upload: "上传",
    download: "下载",
    view: "查看",
    open: "打开",
    add: "新增",
    ok: "好的",
    unnamed: "未命名",
    dash: "-",
    loadFailed: "加载失败",
    networkError: "网络异常，请稍后重试",
    unknownError: "未知错误",
    errorPrefix: "错误：{message}",
    requestFailed: "请求失败（{status}）",
    clearSearch: "清空搜索",
    caseId: "问题 ID",
    caseIdRequired: "问题 ID *",
    case: "问题",
    owner: "负责人",
    stage: "阶段",
    agent: "AI Engineer",
    connection: "连接",
    conversation: "会话",
    revision: "修订版本",
    columnNumber: "第 {index} 列",
    resizeColumn: "调整{column}列宽",
    resizeColumnHint: "拖动调整列宽；方向键微调；Enter、Home 或双击恢复默认宽度",
  },
  format: {
    durationMinutes: "{value} min",
  },
  status: {
    unknown: "未知",
    unknownWithValue: "未知（{value}）",
    run: {
      queued: "未开始",
      validating: "未开始",
      running: "进行中",
      judging: "进行中",
      completed_pending_sync: "进行中",
      completed: "已完成",
      failed: "失败",
      stopping: "进行中",
      stopped: "已停止",
      cancelled: "已停止",
      validationFailed: "校验失败",
      completedIncomplete: "已完成",
    },
    runFilter: {
      running: "运行中",
      active: "进行中",
    },
    cross: {
      queued: "待开始", creating_task: "正在创建采集任务", dispatched: "已下发", running: "采集中", parsing: "正在解析",
      captured: "已采集", captured_partial: "部分采集", evaluating: "评测中", completed: "已完成",
      completed_with_warnings: "完成（有提醒）", partial_failed: "部分失败", retry_wait: "等待重试", failed: "失败", stopping: "停止中", stopped: "已停止",
    },
    governance: {
      draft: "草稿",
      active: "启用",
      archived: "已归档",
      candidate: "候选",
      blocked: "已阻断",
      passed: "通过",
      passed_with_warnings: "通过（有提醒）",
      released: "已发布",
      pending: "待处理",
      editing: "编辑中",
      promoted: "已发布到题库",
      rejected: "已拒绝",
      duplicate: "重复",
      succeeded: "已成功",
      partial_success: "部分成功",
      available: "可用",
      unavailable: "不可用",
      unknown: "未知",
      error: "错误",
      delivered: "已送达",
      retrying: "重试中",
      dead_letter: "死信",
      disabled: "已停用",
      shadow: "影子模式",
      enforcing: "正式阻断",
    },
    candidate: {
      pending: "待加工",
      editing: "编辑中",
      promoted: "已发布到题库",
      rejected: "已拒绝",
      duplicate: "重复",
    },
    syncItem: {
      pending: "待处理",
      running: "处理中",
      imported: "已导入",
      skipped: "已跳过",
      failed: "失败",
      cancelled: "已取消",
    },
    suiteType: {
      release_smoke: "发布冒烟",
      capability_quick: "能力快测",
      region_compatibility: "地区兼容",
      incident_regression: "历史故障回归",
      security_regression: "安全回归",
      full_regression: "全量回归",
      temporary: "临时测试集",
    },
    skipReason: {
      question_disabled: "问题已停用",
      question_archived: "问题已归档",
      suite_item_disabled: "测试集问题已停用",
    },
    projectType: {
      agent: "AI Engineer",
      workflow: "工作流",
      api: "API",
      browser_agent: "浏览器 AI Engineer",
    },
    evaluatorKind: {
      llm_judge: "大模型评判",
      rule_based: "规则评分",
      hybrid: "混合评分",
      manual: "人工评分",
    },
    logLevel: {
      debug: "调试",
      info: "信息",
      warn: "警告",
      warning: "警告",
      error: "错误",
      critical: "严重",
      fatal: "严重",
    },
    stage: {
      dev: "开发",
      qa: "测试",
      staging: "预发",
      production: "生产",
      custom: "自定义",
    },
  },
  nav: {
    overview: "概览",
    projects: "项目",
    newRun: "新建测试任务",
    questionBank: "全部题库",
    suites: "全部测试集",
    queue: "全部测试任务",
    reports: "全部测试报告",
    crossEvaluations: "多源横评",
    logs: "日志",
    settings: "设置",
  },
  route: {
    parent: {
      runs: "运行",
      tasks: "测试任务",
      results: "测试结果",
      reports: "测试报告",
      queue: "运行队列",
    },
  },
  shell: {
    documentTitle: "{page} · Agent Judge",
    brandSubtitle: "AI Engineer 测试平台",
    brandAria: "Agent Judge 测试工作台",
    primaryNavAria: "主导航",
    breadcrumbAria: "当前位置",
    navGroup: { workspace: "工作空间", testAssets: "测试资产", crossProject: "跨项目查看" },
    skipToContent: "跳到主要内容",
    topbarContext: "AI Engineer 测试工作台",
    collapse: "收起",
    collapseSidebar: "收起侧边栏",
    expandSidebar: "展开侧边栏",
    accountSecurity: "账号安全",
    logout: "退出登录",
    loggingOut: "正在退出…",
    logoutFailed: "退出失败，当前仍保持登录：{message}",
    logoutFailedFallback: "请检查网络后重试",
    routeLoading: "正在加载产品治理页面…",
    title: {
      overview: "概览",
      evaluationProfiles: "评测方案",
      prompts: "评分提示词",
      targets: "运行目标",
      projectQuestions: "题库",
      productionSamples: "真实会话",
      dataSync: "数据同步",
      projectContent: "测试集",
      projectRuns: "运行",
      projectTasks: "测试任务",
      projectResults: "测试结果",
      projectReports: "测试报告",
      coverage: "能力覆盖",
      gatePolicy: "门禁策略",
      gateNotifications: "门禁通知",
      projectSettings: "项目设置",
      projects: "项目",
      reports: "全部测试报告",
      questionBank: "全部题库",
      suites: "全部测试集",
      queue: "全部测试任务",
      logs: "日志",
      security: "账号安全",
      migrated: "配置已迁移",
      settings: "设置",
      newRun: "新建测试任务",
      failures: "失败复核",
      runDetail: "运行详情",
      crossEvaluations: "多源横评",
      notFound: "页面不存在",
    },
  },
  api: {
    networkFailed: "网络连接失败",
    downloadNetworkFailed: "网络连接失败，无法下载文件",
  },
  auth: {
    workspaceLabel: "AI 评测工作台",
    kicker: "INDUSTRIALMIND 评测平台",
    poweredBy: "由 IndustrialMind AI 评测平台提供支持",
    headlineLead: "让每一次 AI Engineer 变更",
    headlineEmphasis: "都经得起评测",
    headlineDescription: "从题库和测试集，到运行执行、Judge 判定与报告分析，在一个工作台中建立可复现的质量闭环。",
    flowAria: "AI Engineer 评测流程",
    formPanelAria: "Agent Judge 登录",
    eyebrow: "安全评测工作台",
    title: "欢迎回来",
    description: "登录后继续管理评测运行与质量报告。",
    bootstrapEyebrow: "首次设置",
    bootstrapTitle: "初始化管理员",
    bootstrapDescription: "创建本地数据库中的首个管理员账号。",
    platformEyebrow: "统一身份认证",
    platformTitle: "使用 IM Production 登录",
    platformDescription: "Agent Judge 将复用当前 IM Production 账号和租户身份。",
    platformGuidance: "如果尚未登录，请在新标签页完成 IM Production 登录；完成后本页面会自动重新检查。",
    platformOpenLogin: "打开 IM Production 登录",
    platformRecheck: "重新检查登录状态",
    platformTrustNote: "登录凭据由 IM Production 验证，Agent Judge 不保存访问令牌",
    platformSessionExpired: "IM Production 登录已失效，请重新登录",
    platformAccessDenied: "当前 IM Production 租户无权访问 Agent Judge，请切换到已授权租户",
    username: "用户名",
    password: "密码",
    showPassword: "显示密码",
    hidePassword: "隐藏密码",
    verifying: "正在验证…",
    submit: "登录 Agent Judge",
    bootstrapSubmit: "创建管理员并进入",
    trustNote: "凭据仅用于本地安全会话认证",
    loginFailed: "登录失败，请重试",
    sessionExpired: "会话已过期，请重新登录",
    step: {
      questionBank: "题库",
      suites: "测试集",
      runs: "运行",
      reports: "测试报告",
    },
    gate: {
      checking: "正在验证登录状态",
      pleaseWait: "请稍候…",
      unreachableTitle: "无法连接 Agent Judge 后端",
      unreachable: "暂时无法连接 Agent Judge 后端。",
      offline: "网络连接中断，请检查本机网络，或确认 Agent Judge 后端仍在运行。",
      reconnect: "重新连接",
    },
  },
  confirm: {
    pending: "处理中…",
  },
  guard: {
    discardTitle: "放弃未保存的修改？",
    discardMessage: "当前有未保存的修改。离开后草稿会丢失。",
    stayEditing: "留下继续编辑",
    discardAndLeave: "丢弃并离开",
  },
  pager: {
    aria: "分页",
    summary: "第 {page} / {totalPages} 页 · 共 {total} 条",
    total: "共 {total} 条",
    page: "{page} / {totalPages}",
    pageSize: "每页",
    pageSizeAria: "每页显示条数",
    previous: "上一页",
    next: "下一页",
  },
  governance: {
    copyId: "复制 ID",
    copyIdAria: "{label}：{value}",
    idCopied: "ID 已复制",
    copyIdFailed: "复制失败，请手动选择",
    readOnlyTitle: "只读项目",
    readOnlyDescription: "该项目只用于查看历史资产和运行记录，不能新增或编辑内容。",
    metricAria: "{label}：{value}",
    tabsAria: "项目功能导航",
    settingsNavAria: "项目设置分区",
    unsavedMarker: "有未保存修改",
    tab: {
      overview: "项目概览",
      content: "测试集",
      productionSamples: "真实会话",
      runs: "运行",
      tasks: "测试任务",
      results: "测试结果",
      reports: "测试报告",
      settings: "设置",
    },
    settingsNav: {
      defaults: "项目默认",
      env: "运行目标",
      eval: "评测方案",
      prompts: "评分提示词",
      sync: "数据同步",
    },
  },
  settingsReadiness: {
    env: {
      label: "运行目标",
      ready: "{count} 个启用中的运行目标",
      missing: "需要至少一个启用中的运行目标",
    },
    defaultProfile: {
      label: "默认评测方案",
      ready: "已设置：{name}",
      missing: "未设置；新建测试任务不会预选评测方案",
    },
    judgeKey: {
      label: "评分模型凭证",
      needProfile: "先设置默认评测方案",
      ready: "服务地址与密钥已配置",
      missingUrl: "缺少评分模型服务地址",
      missingKey: "缺少评分模型密钥",
    },
    prompt: {
      label: "评分提示词",
      referenced: "默认配置引用：{name}",
      referencedUnnamed: "已指定提示词引用",
      optional: "可使用系统/项目提示词（可选）",
      none: "尚无项目提示词；运行时可使用系统默认",
    },
  },
  importSteps: {
    aria: "导入步骤",
    parse: "解析文件",
    conflicts: "冲突检查",
    commit: "确认写入",
  },
  notFound: {
    title: "页面不存在",
    description: "当前地址没有对应页面，可能是链接已失效或地址输入有误。",
    goBack: "返回上一页",
    goHome: "回到评测首页",
  },
  migrationPlaceholder: {
    description: "该页面正在迁移到 React，后端 API 与数据库结构保持不变。",
  },
  logs: {
    redactedPlaceholder: "[已脱敏]",
    circularPlaceholder: "[循环引用]",
  },
  runs: {
    sortAria: "{label}，{state}",
    sortAsc: "升序",
    sortDesc: "降序",
    sortIdle: "点击排序",
    duration: {
      seconds: "{value} 秒",
      minutes: "{value} 分钟",
      hours: "{value} 小时",
    },
    progress: {
      aria: "运行进度 {percent}%",
      notStarted: "未开始",
      notStartedAria: "运行尚未开始，计划 {total} 步",
    },
    actions: {
      requestFailed: "请求失败，请稍后重试",
      stopped: "运行已停止",
      stopFailed: "停止运行失败：{message}",
      resumed: "续跑运行已提交",
      resumeFailed: "续跑运行失败：{message}",
      deleted: "已删除运行「{name}」",
      deleteFailed: "删除运行失败：{message}",
    },
  },
  runDetail: {
    knowledgeSourceFallback: "知识库来源",
    sourceDocuments: "来源文档（{count}）",
    answerLoading: "正在整理答案…",
    turnCountSuffix: "（共 {count} 轮）",
    skipReasonFallback: "创建运行时不可执行",
    judgePromptFallbackName: "评估提示词",
    alignment: {
      match: "一致",
      mismatch: "不一致",
    },
    integrity: {
      placeholderAnswer: "结果不完整：最终回答仍为占位文案（如「正在整理答案…」）",
      missingAnswer: "结果不完整：缺少用户可见最终回答",
      inconsistent: "一致性分 {percent}%，低于本次运行阈值",
    },
    attention: {
      failed: "聚合未达标问题",
      incomplete: "结果不完整",
      consistency: "一致性 {percent}%",
      consistencyPending: "一致性待核验",
      skipped: "问题跳过",
    },
    promptSource: {
      unknown: "未知来源",
      run_override: "本次运行临时覆盖",
      project_prompt: "项目提示词库",
      profile_recommended: "评测方案推荐",
      agent_selected: "AI Engineer 选用",
      agent_default: "AI Engineer 默认",
      env_prompt_store: "环境提示词库",
      system_default: "系统默认",
    },
    table: {
      noResults: "当前运行还没有结果明细。",
      noFilteredResults: "当前筛选条件下没有结果。",
      searchLabel: "搜索结果",
      filtersAria: "结果筛选工具栏",
      searchPlaceholder: "问题 ID 或问题",
      statusLabel: "结果状态",
      count: "{shown} / {total} 条",
      defaultSort: "默认排序：问题 ID 升序 · 同一问题按重复回答序号",
      clearFilters: "清除筛选",
      runIndex: "第 {index} 次回答",
      completion: "完成 {done}/{total}（{percent}）",
      passed: "通过",
      openDetail: "查看明细",
      openDetailAria: "查看明细：问题 {caseId} 第 {index} 次回答",
      retryCase: "仅重试本题",
      retrying: "重试中",
      retriedTimes: "已重试 {count} 次",
      turnLogs: "该轮日志",
      paginationAria: "结果分页",
      range: "第 {from}-{to} 条，共 {total} 条",
      status: {
        attention: "需关注",
        failed: "未通过",
        incomplete: "结果不完整",
        inconsistent: "一致性不足",
        passed: "已通过",
      },
      column: {
        run: "运行",
        question: "问题",
        executedAverage: "已执行轮次平均分",
        completion: "完成率",
        result: "结果",
        duration: "耗时",
        actions: "操作",
      },
    },
    dialog: {
      title: "问题 {caseId} · 第 {index} 次回答",
      description: "已执行轮次平均分 {average} · 完成 {done}/{total} 轮 · {result}",
      reportTitle: "问题详情 · {question}", reportDescription: "按次完整展示回答、评分结论、耗时、Token 与运行时证据。", repeatAnswersAria: "重复回答选择", repeatAnswers: "查看回答", platformStability: "平台稳定性", answerStability: "回答稳定性", collectionTime: "采集耗时", completeAnswerTime: "完整回答时间", tokenUsage: "Token 用量", repeatAverageScore: "问题质量分", qualityDimensions: "七个质量维度", successCount: "{completed} / {total} 成功", questionAndEvidence: "问题与评分依据", questionAndGroundTruth: "问题与标准答案", question: "问题", groundTruth: "标准答案", answerContent: "回答内容", answerCardAria: "第 {index} 次回答详情", judgeConclusion: "评分结论", completeRepeatAnswers: "重复回答详情", bestScoreSuffix: " · 本报告最高分", qualityScoreShort: "问题质量分", fullTimeShort: "完整回答", firstResponseShort: "首次响应", toolTimeShort: "工具调用", openTargetSession: "打开目标会话", openTargetSessionAria: "在新窗口打开第 {index} 次回答的目标会话", citationEvidence: "引用证据", evidenceItem: "证据项", runtimeSnapshotAndPrompt: "本报告运行时版本与提示词", report: "报告", platformAgentVersion: "平台 / AI Engineer 版本", targetPromptVersion: "待评测平台提示词版本", repeatConfig: "重复配置", repeatConfigValue: "{count} 次 / 问题", targetPromptSnapshot: "待评测平台提示词快照",
      closeLabel: "关闭问题明细",
      turnLabel: "第 {index} 轮",
      previousCase: "上一条",
      previousCaseAria: "上一条问题",
      nextCase: "下一条",
      nextCaseAria: "下一条问题",
      queuePosition: "队列第 {position} / {total} 条",
      openQuestion: "打开问题",
      openSuite: "打开测试集",
      openRun: "查看原运行",
      tabsAria: "问题明细分区",
      artifactCard: "数据卡片",
      artifactPaginationAria: "数据卡片分页",
      artifactPage: "第 {page} / {pageCount} 页 · 当前显示 {rows} 行",
      rowCount: "{count} 行",
      columnSummary: "列摘要：{summary}",
      columnJoin: "、",
      columnCount: "（{count} 列）",
      moreColumns: " 等 {count} 列",
      noDeclaredColumns: "未声明列",
      noVisibleData: "暂无可见数据",
      eventsLoadFailed: "事件加载失败",
      eventsLoading: "正在加载事件…",
      noEvents: "暂无事件记录。",
      eventDiscarded: "已丢弃",
      eventTruncated: "已截断",
      showFailedAttempts: "显示失败 attempt",
      eventSummaryAria: "事件摘要",
      missingRunId: "缺少运行标识，无法加载事件。",
      toolTrace: "工具链路（{count} 条，不参与最终回答评分）",
      selectTurn: "查看轮次",
      standardAnswer: "标准答案",
      agentAnswer: "AI Engineer 回答",
      repeatDiffAria: "重复回答差异",
      repeatDiffTitle: "重复回答对比",
      currentSuffix: "（当前）",
      scorePoints: "{score} 分",
      notScored: "未评分",
      blockedByPrevious: "前序未通过，未执行",
      blockedByPreviousReason: "前序轮次未达到通过阈值，本轮未执行。",
      evidenceIncomplete: "证据不完整",
      userInputAndAnswer: "用户输入 / 标准答案",
      visibleFinalAnswer: "用户可见最终回答",
      retryHistory: "技术重试历史",
      retryHistoryItem: "第 {index} 次回答 · {status} · 得分 {score}",
      retryFixed: "已修复",
      retryStillIncomplete: "仍不完整",
      incompleteReason: "未完成原因",
      issues: "问题明细",
      noIssues: "暂无问题明细。",
      noIssuesWorkbench: "暂无 Judge 缺点、完整性或一致性异常。",
      nartAlignment: "NART 对齐",
      nartDimension: "维度",
      nartResult: "结果",
      tab: {
        answers: "回答对比",
        answersWithCount: "回答对比（{count} 遍）",
        turns: "轮次证据（{count}）",
        artifacts: "数据卡片（{count}）",
        events: "事件链路",
        issues: "问题明细（{count}）",
      },
    },
  },
  productionTags: {
    groupAria: "题库标签",
    optionsAria: "{label}选项",
    selectedAria: "已选标签",
    search: "搜索标签",
    removeAria: "移除 {label}",
    noneSelected: "尚未选择标签",
    noMatch: "没有匹配的标签",
    disabledSuffix: "（已停用）",
  },
  suites: {
    upload: {
      onlyXlsx: "当前仅支持 .xlsx Excel 测试集",
      selectProject: "请选择测试集所属项目",
      uploading: "正在上传 Excel 文件",
      parsed: "解析完成，请确认后写入题库并创建测试集",
      parsedWithConflicts: "解析完成，发现 {count} 个冲突，请前往题库复核",
      failed: "测试集导入失败",
    },
    import: {
      panelAria: "导入测试集",
      fileInputAria: "选择 Excel 测试集文件",
      selectProjectFirst: "请先选择所属项目",
      chooseFile: "选择 Excel 文件",
      start: "开始导入",
      parsing: "解析中…{elapsed}",
      parsingElapsed: "正在解析文件，已用时 {elapsed}",
      parsedNote: "解析完成，请在项目题库中复核冲突并确认写入。",
      readyNote: "解析后会进入题库冲突复核，确认写入才会创建测试集。",
      downloadTemplate: "下载统一 Excel 模板",
      templateAfterProject: "选择项目后可下载模板",
      guideTemplateTitle: "统一模板：",
      guideTemplateBody: "包含「问题」「对话轮次」「可用标签」「填写说明」四个工作表，同时支持单轮和多轮。",
      guideRequiredTitle: "必填内容：",
      guideRequiredBody: "“问题”工作表填写问题 ID；“对话轮次”工作表填写问题 ID、连续轮次、用户输入和标准答案。同一问题有 1 行为单轮，2 行及以上为多轮。",
      guideNote: "标签只填写在“问题”工作表。仅支持不超过 50 MB 的 .xlsx 文件；解析后进入题库冲突复核，确认后写入题库并创建测试集。",
    },
    caseEditor: {
      createTitle: "添加问题",
      editTitle: "编辑问题",
      description: "维护单轮问题的问题、标准答案和标签。评分阈值在创建评测运行时统一设置。按 Ctrl/⌘ + Enter 保存。",
      draftUnsaved: "草稿未保存",
      save: "保存问题",
      saving: "保存中",
      requiredError: "问题 ID 和用户问题不能为空",
      caseIdLabel: "问题 ID *",
      caseIdHelp: "测试集内唯一，创建后不可修改。",
      caseIdRequired: "问题 ID 必填。",
      questionLabel: "用户问题*",
      questionHelp: "发送给被测系统的输入。",
      questionRequired: "用户问题必填。",
      answerLabel: "标准答案",
      answerHelp: "提供给 Judge 作为参考事实。留空仍会评分，但只能依据问题和当前 Judge Prompt 或默认规则，建议填写以减少歧义。",
      tagsLabel: "标签",
      tagsPlaceholder: "多个标签使用逗号分隔",
      enableCase: "启用该问题",
      discardTitle: "放弃未保存的问题草稿？",
      discardDescription: "关闭编辑器后，本次填写的问题、标准答案与标签都会丢失。",
      discardConfirm: "放弃草稿",
      discardCancel: "继续编辑",
    },
  },
  settings: {
    discovery: {
      subjectTenants: "获取租户",
      subjectAgents: "获取 AI Engineer",
      offline: "无法连接服务，{subject}失败，请确认本地后端仍在运行",
      forbidden: "登录失败或当前账号没有权限{subject}",
      failed: "{subject}失败",
    },
    field: {
      agentFrontendUrl: "AI Engineer 会话页面 URL",
      platformLoginUrl: "平台登录页 URL",
      loginUrl: "登录 URL",
      agentTimeout: "AI Engineer 超时",
      agentConcurrency: "AI Engineer 并发",
      agentRetry: "AI Engineer 失败重试",
      judgeConcurrency: "Judge 并发",
      judgeRetry: "Judge 失败重试",
      repeatRuns: "默认重复运行次数",
      repeatConcurrency: "单问题重复并发",
      maxScore: "总分满分",
    },
    validation: {
      httpUrl: "{label}必须是以 http:// 或 https:// 开头的完整 URL",
      integerAtLeast: "{label}必须是大于等于 {minimum} 的整数",
      integerBetween: "{label}必须是 {minimum} 到 {maximum} 之间的整数",
      judgeModelRequired: "Judge 模型不能为空",
      tenantRequired: "请选择当前环境的默认租户",
      repeatRunsMax: "默认重复运行次数不能超过 50",
      repeatModeInvalid: "重复执行方式无效",
      passThresholdRange: "及格线必须在 0 到 100 之间",
      consistencyRange: "一致性阈值必须在 0 到 100 之间",
      passThresholdAboveMax: "及格线不能高于总分满分",
      scoringRuleRequired: "请选择打分规则",
      platformLoginUrlRequired: "请输入完整的平台登录页 URL",
      loginUsernameRequired: "请输入登录用户名",
      loginPasswordRequired: "请输入登录密码",
      environmentNamePattern: "环境 ID 仅支持 1–50 位英文字母、数字、下划线或连字符，且需以字母或数字开头",
      environmentNameTaken: "环境「{name}」已存在",
    },
  },
  agentEditor: {
    eyebrow: "AI Engineer 配置",
    title: "当前环境的 AI Engineer 接入",
    add: "新增 AI Engineer",
    empty: "当前环境没有 AI Engineer，请先新增。",
    promptCustom: "自定义",
    promptInherit: "继承环境",
    setDefaultAria: "设 {key} 为默认 AI Engineer",
    createTitle: "新增 AI Engineer",
    editTitle: "编辑 AI Engineer",
    description: "保存后仍需提交环境设置才会生效。",
    dirtyDescription: "草稿未保存；保存后仍需提交环境设置才会生效。",
    save: "保存 AI Engineer",
    noChanges: "无变更",
    duplicateCode: "AI Engineer 编码「{code}」已存在，请使用其他编码。",
    discardTitle: "放弃未保存的 AI Engineer 草稿？",
    discardDescription: "关闭编辑器会丢弃本次填写的名称、ID、编码与提示词，已保存的 AI Engineer 不受影响。",
    discardConfirm: "丢弃草稿",
    removeTitle: "从草稿中删除 AI Engineer「{key}」？",
    removeTitleFallback: "删除 AI Engineer？",
    removeDetails: "本操作只修改当前草稿；点「保存设置」后才会写入环境，在此之前可重新加载页面撤销。",
    removeConfirm: "从草稿中删除",
    removeImpactNone: "当前默认 AI Engineer 不受影响。",
    removeImpactSwitch: "它是当前默认 AI Engineer；删除后默认 AI Engineer 将切换为「{next}」。",
    removeImpactLast: "它是当前唯一且默认的 AI Engineer；删除后将无法创建新评测，直到重新添加 AI Engineer。",
    column: {
      name: "名称",
      id: "AI Engineer ID",
      agentCode: "AI Engineer 编码",
      engineerCode: "工程师编码",
      judgePrompt: "评分提示词",
      default: "默认",
      actions: "操作",
    },
    field: {
      name: "AI Engineer 名称",
      id: "AI Engineer ID *",
      agentCode: "AI Engineer 编码 *",
      engineerCode: "工程师编码",
      judgePrompt: "AI Engineer 专用评分提示词（留空继承环境默认）",
    },
  },
  connectionSetup: {
    stepperAria: "AI Engineer 接入步骤",
    step1: "连接账号",
    step2: "选择租户范围",
    urlInvalid: "请填写以 http:// 或 https:// 开头的完整地址。",
    credentialsTitle: "1. 连接平台账号",
    credentialsDescription: "验证账号后自动读取可访问的租户，登录凭据不会显示在发现结果中。",
    verified: "已验证",
    saved: "已保存",
    pendingConnect: "待连接",
    platformLoginHelp: "填写浏览器登录页地址；Token URL 和 AI Engineer API 地址会在连接后自动回填。",
    loginUsername: "登录用户名",
    loginPassword: "登录密码",
    passwordSavedPlaceholder: "已保存；留空继续使用",
    passwordPlaceholder: "请输入密码",
    passwordHelp: "更换用户名时必须重新输入密码。",
    usingSavedPassword: "将使用当前环境已保存的密码",
    ready: "准备就绪",
    fetchTenants: "连接并获取租户",
    fetchingTenants: "正在获取租户…",
    tenantTitle: "2. 选择默认租户",
    tenantDescription: "环境保存账号可访问的租户范围；默认租户只用于新建运行时预选。",
    selected: "已选择",
    pendingSelect: "待选择",
    searchTenants: "搜索租户",
    searchTenantsPlaceholder: "搜索名称、Key、ID 或角色",
    availableTenants: "可用租户",
    noTenantMatch: "没有匹配的租户",
    tenantSummary: "默认租户：{name}；共保存 {count} 个可访问租户",
    selectTenantHint: "请选择一个默认租户后保存环境",
    savedTenant: "已保存租户：{name}。重新连接后可查看或切换租户。",
    connectFirst: "先完成账号连接，租户会显示在这里。",
    agentUnbound: "AI Engineer 不再绑定到环境。新建测试任务时，系统会按所选租户实时加载 AI Engineer，并支持一次选择多个 AI Engineer。",
    errorHint: "{message}。输入和已完成的选择仍会保留，可直接重试。",
    readyTitle: "环境连接已就绪",
    defaultTenant: "默认租户：{name}",
    tenantRecorded: "已记录 {count} 个可访问租户；AI Engineer 在创建运行时选择",
    saveConnection: "保存环境连接",
    advanced: "高级设置与手工接入",
    advancedHint: "仅在平台不支持自动发现或需要覆盖默认地址时使用。修改后仍需点击保存。",
    tenantKey: "租户 Key",
    tenantId: "租户 ID",
    agentBaseUrlHelp: "目标 AI Engineer 的 API 网关根地址。",
    agentBaseUrlLabel: "AI Engineer API 根地址",
    tokenUrlLabel: "Token 地址",
    agentTimeoutSeconds: "AI Engineer 超时（秒）",
    agentTimeoutHelp: "单次 AI Engineer 调用等待上限，超时按失败计入运行结果。",
    tokenUrlHelp: "运行时获取访问 Token 的接口，不是浏览器登录页。",
    agentFrontendUrlHelp: "用于在报告里回链到 AI Engineer 原始会话，留空则不生成回链。",
  },
  promptSettings: {
    loading: "正在加载 Prompt 配置…",
    loadFailed: "Prompt 配置加载失败：{message}",
    capsuleEyebrow: "胶囊配置",
    capsuleTitle: "Prompt 胶囊",
    configNotApplied: "配置未生效",
    cancelConfigChanges: "取消配置更改",
    saveConfig: "保存胶囊配置",
    configSaved: "胶囊配置已保存并生效",
    configSaveFailed: "胶囊配置保存失败：{message}",
    configErrorHint: "{message}。更改仍保留，可重试或取消。",
    enableCapsule: "启用 {name}",
    editorEyebrow: "编辑器",
    editorFallbackTitle: "Prompt 编辑",
    activeVersion: "当前生效版本",
    editingNewDraft: "正在编辑新版本草稿",
    selectPrompt: "请选择 Prompt",
    versionHelp: "切换版本后需保存胶囊配置才会生效。",
    name: "名称",
    description: "说明",
    body: "Prompt 正文",
    insertVariable: "点击插入变量：",
    undeclaredVariables: "未声明变量：{variables}。请删除或改用上方变量。",
    undeclaredVariablesToast: "存在未声明变量：{variables}",
    fixVariablesFirst: "请先处理未声明变量",
    restoreDefault: "恢复并保存默认",
    cancelDraft: "取消草稿",
    createPrompt: "创建 Prompt",
    savePrompt: "保存 Prompt",
    promptCreated: "Prompt 已创建；保存胶囊配置后生效",
    promptSaved: "Prompt 已保存",
    promptDeleted: "Prompt 已删除",
    promptRestored: "默认 Prompt 已恢复",
    copyName: "{name} 副本",
    newVersionName: "{name} 新版本",
    copyAnnouncement: "已创建 Prompt 副本草稿，请先确认名称。",
    newVersionAnnouncement: "已创建 Prompt 新版本草稿，请先填写名称。",
    discardTitle: "放弃未保存的 Prompt 草稿？",
    discardUrlChange: "地址栏指向了另一个胶囊或版本。切换会丢弃当前编辑器里未保存的名称、说明与正文，已保存的版本不受影响。",
    discardSwitchCapsule: "切换胶囊会丢弃当前编辑器里未保存的名称、说明与正文，已保存的版本不受影响。",
    discardSwitchVersion: "切换版本会丢弃当前编辑器里未保存的名称、说明与正文，已保存的版本不受影响。",
    discardCreate: "新建版本会丢弃当前编辑器里未保存的名称、说明与正文，已保存的版本不受影响。",
    keepDraft: "留在当前草稿",
    discardAndContinue: "丢弃草稿并继续",
    currentCapsule: "当前胶囊",
    restoreTitle: "恢复「{name}」的系统默认 Prompt？",
    restoreDescription: "恢复会立即写入系统默认 Prompt 并覆盖该胶囊的当前选择。",
    restoreDescriptionDirty: "恢复会立即写入系统默认 Prompt 并覆盖该胶囊的当前选择，同时丢弃编辑器里未保存的草稿。",
    restoreDetails: "该操作立即生效且不可撤销；历史运行记录仍保留原来使用的 Prompt 快照。",
    restoring: "恢复中…",
    deleteTitle: "删除 Prompt「{name}」？",
    deleteDescription: "删除后该版本不再出现在版本列表中，若它是当前生效版本，胶囊会回退到其他版本。",
    deleteDetails: "该操作不可撤销；已完成的运行仍保留当时的 Prompt 快照。",
    deletePrompt: "删除 Prompt",
    deleting: "删除中…",
  },
  security: {
    managedByPlatformEyebrow: "统一身份",
    managedByPlatformTitle: "账号由 IM Production 管理",
    managedByPlatformDescription: "当前使用统一登录。头像、密码和账号资料请在 IM Production 中维护，Agent Judge 不会另存一份密码。",
    platformAccount: "平台账号",
    platformTenant: "租户",
    accountProfile: "账号资料",
    adminAvatar: "管理员头像",
    currentAvatarAlt: "当前头像",
    chooseAvatar: "选择头像图片",
    avatarSaving: "正在保存头像…",
    removeAvatar: "移除头像",
    avatarHelp: "支持 PNG、JPG、WebP、GIF，最大 10 MB；选择后会居中裁切并立即保存。",
    avatarNotImage: "请选择图片文件",
    avatarTooLarge: "图片不能超过 10 MB",
    avatarProcessFailed: "头像处理失败",
    avatarProcessUnsupported: "浏览器无法处理头像图片",
    avatarUpdated: "头像已更新",
    removeAvatarTitle: "移除当前管理员头像？",
    removeAvatarDescription: "确认后立即写入服务端并清除已保存的头像图片，头像会恢复为用户名首字母。此操作不可撤销，需要重新上传图片才能恢复。",
    removeAvatarDetails: "账号密码与登录会话不受影响。",
    keepAvatar: "保留头像",
    confirmRemoveAvatar: "确认移除头像",
    removingAvatar: "移除中…",
    passwordEyebrow: "密码",
    changePassword: "修改密码",
    changing: "修改中…",
    currentUsernameAria: "当前账号用户名",
    currentPassword: "当前密码",
    newPassword: "新密码（至少 8 位）",
    confirmPassword: "确认新密码",
    strength: "强度：{level}",
    strengthEmpty: "尚未输入",
    strengthShort: "长度不足",
    strengthStrong: "较强",
    strengthFair: "一般",
    passwordAdvice: "建议 12 位以上，并混合大小写字母、数字和符号。",
    passwordMismatch: "两次输入的新密码不一致",
    passwordSameAsCurrent: "新密码不能与当前密码相同",
    passwordChanged: "账号密码修改成功，所有会话已退出，请使用新密码登录",
    currentPasswordWrong: "当前密码不正确，请重新输入",
  },
  tags: {
    legend: "标签",
    actionFailed: "操作失败，请稍后重试",
    loadingTags: "加载标签",
    facetLoadFailed: "标签筛选加载失败",
    facetUnavailable: "当前标签筛选已不可用",
    noTags: "当前问题尚未设置标签",
    clearFilter: "清除标签筛选",
    searchFacetAria: "搜索筛选标签",
    searchPlaceholder: "搜索标签",
    searchAvailableAria: "搜索可用标签",
    searchTagOrDimension: "搜索标签或维度",
    groupFilterAria: "{name}筛选",
    questionCount: "{count} 道题",
    collapse: "收起",
    expandAll: "展开全部（{count}）",
    noMatch: "没有匹配的标签",
    selectedAria: "已选标签",
    noneSelected: "尚未选择标签",
    disabled: "已停用",
    disabledSuffix: "（已停用）",
    removeAria: "移除 {label}",
    creating: "创建中…",
    createAndSelect: "创建项目标签「{label}」并选中",
    sameNameDisabled: "同名标签已停用，请先在标签管理中重新启用。",
    sameNameExists: "已有同名标签，可直接选择。",
    reusedExisting: "已使用现有标签",
    created: "项目标签已创建",
    renamed: "标签已重命名，标签 ID 保持不变",
    enabledToast: "标签已启用",
    disabledToast: "标签已停用，历史引用保持不变",
    deleted: "未使用标签已删除",
    managerTitle: "项目标签管理",
    managerDescription: "维护当前项目可用标签；工作区标准标签只能在本项目启用或禁用。",
    managerClose: "关闭标签管理",
    scopeFilterAria: "筛选标签作用域",
    scopeAll: "全部作用域",
    scopeProject: "项目标签",
    scopeWorkspace: "工作区标准",
    scopeProjectShort: "项目",
    scopeWorkspaceShort: "工作区",
    statusFilterAria: "筛选标签状态",
    statusAll: "全部状态",
    statusActive: "已启用",
    statusDisabled: "已停用",
    newProjectTag: "新建项目标签",
    newTagPlaceholder: "例如：库存专项",
    readOnlyNote: "当前项目为只读，只能查看标签。",
    loadingProjectTags: "正在加载项目标签",
    loadProjectTagsFailed: "项目标签加载失败",
    rename: "重命名",
    renameAria: "重命名 {label}",
    disable: "停用",
    reEnable: "重新启用",
    noMatchingTags: "没有符合条件的标签",
    noProjectTags: "还没有项目标签",
    noVisibleTags: "当前项目没有可查看的标签。",
    createFirstTag: "可以新建第一个项目标签。",
    renameTitle: "重命名标签",
    renameDescription: "只改变展示名称，不修改历史标签 ID。",
    saveName: "保存名称",
    maxLengthHint: "最多 80 个字符。",
    labelRequired: "标签名称必填。",
    deleteTitle: "删除这个标签？",
    deleteDescription: "该标签没有任何问题版本引用，删除后无法恢复。",
    deleteConfirm: "删除标签",
    disableTitle: "停用这个标签？",
    disableDescription: "该标签仍有历史问题版本引用。停用后不能再分配，历史内容继续保留。",
    disableDescriptionUsed: "当前有 {count} 道有效问题引用。停用后不能再分配，但历史内容继续保留。",
    disableConfirm: "停用标签",
    discardTitle: "放弃未创建的标签名称？",
    discardDescription: "「{label}」尚未创建，关闭标签管理后会丢失。",
    discardConfirm: "放弃并关闭",
    column: {
      label: "标签名称",
      dimension: "维度",
      scope: "作用域",
      questions: "当前问题",
      status: "状态",
    },
  },
  settingsPage: {
    title: "设置中心",
    description: "此处只保留账号安全、输出与存储。运行参数、评分规则和提示词请在项目内维护。",
    unsaved: "有未保存更改",
    navAria: "设置分类",
    migratedSection: "配置已迁移",
    securityGuard: "密码表单尚未提交，离开后已输入的当前密码与新密码会被清空，需要重新填写。",
    backToRunDetail: "返回运行详情",
    backToNewRun: "返回新建测试任务",
    backToReports: "返回报告",
    backToLogs: "返回日志",
    backToQueue: "返回运行队列",
    bannerTitle: "项目级评测方案已迁移",
    bannerDescription: "运行参数 / 评分规则 / 提示词已迁到项目内评测方案与提示词库，不再通过旧的环境设置中心维护。",
    migratedTitle: "运行参数 / 评分规则 / 提示词已迁移",
    migratedDescription: "这些配置现在归属项目内「评测方案」与「提示词库」。请从项目页进入对应项目后维护，不再通过旧的环境切换管理。",
    goToProjects: "前往项目",
    offline: "无法连接服务，请检查网络或确认本地后端仍在运行",
    checkFailedStale: "本次检查失败；下方为 {time} 成功获取的数据。{message}",
    checkFailed: "无法获取存储状态。{message}",
    rechecking: "正在重新检查；当前显示 {time} 获取的数据。",
    checkedAt: "{provider} · {time} 已完成只读检查",
    loadingStatus: "正在读取平台状态",
    checkingStorage: "正在检查存储连接",
    storageUnavailable: "存储状态暂不可用",
    recheckingStorage: "正在重新检查存储连接",
    storageOk: "存储连接正常",
    checking: "检查中…",
    recheck: "重新检查",
    awaitingCheck: "等待检查",
    lastSuccessfulCheck: "上次成功检查：{time}。",
    storageService: "存储服务",
    storageServiceNote: "环境配置、测试集和运行记录使用同一远程存储。",
    currentData: "当前数据",
    currentDataValue: "{suites} 个测试集 · {runs} 个运行",
    currentDataNote: "数量来自当前数据库的只读查询。",
    executor: "运行调度器",
    executorWorkers: "PostgreSQL 队列 · {count} 个 Worker 在线",
    executorInline: "Web 进程线程（兼容模式）",
    executorQueue: "{queued} 个排队、{active} 个执行中；运行支持 lease/heartbeat 恢复。",
    executorHint: "可在部署时切换到 postgres_worker 生产模式。",
    export: "结果导出",
    exportFormats: "JSON、Excel",
    exportNote: "支持完整结果和失败问题范围。",
    logQuery: "日志查询",
    logQueryValue: "页面筛选、JSONL 导出",
    logQueryNote: "可按运行、问题、运行遍次、类型和日期定位。",
    viewResults: "查看运行结果",
    viewLogs: "查询日志",
    section: {
      security: "账号安全",
      output: "输出与存储",
    },
  },
  projectRuns: {
    title: "{name} · 运行",
    description: "跟踪当前项目的评测运行进度；需要开跑时用「新建测试任务」。",
    openQueue: "打开运行队列",
    loadingTitle: "正在加载项目运行",
    loadingDescription: "正在读取项目运行状态。",
    errorTitle: "项目运行加载失败",
    emptyTitle: "暂无项目运行",
    emptyDescription: "使用当前项目的测试集创建评测后，运行记录会显示在这里。",
    tableLabel: "项目运行",
    column: {
      run: "运行",
      status: "执行状态",
    },
  },
  projectSuites: {
    title: "{name} · 测试集",
    description: "这里管理可以直接发起评测的测试集；需要增删问题时，从题库创建新的测试集。",
    questionBank: "题库",
    composeFromBank: "从题库创建测试集",
    loadingTitle: "正在加载项目测试集",
    loadingDescription: "正在按项目归属读取测试资产。",
    errorTitle: "测试集加载失败",
    emptyTitle: "暂无项目测试集",
    emptyDescription: "先在题库维护问题，再从已选问题创建测试集。",
    emptyAction: "前往题库创建测试集",
    tableLabel: "项目测试集",
    createRun: "创建评测",
    tableMeta: "共 {count} 个测试集 · 排序：{sort}",
    searchAria: "搜索测试集",
    searchPlaceholder: "搜索测试集名称或 ID",
    typeFilterAria: "按测试集类型筛选",
    allTypes: "全部类型",
    statusFilterAria: "按测试集状态筛选",
    noMatchTitle: "没有匹配的测试集",
    noMatchDescription: "请调整搜索关键词、类型或状态筛选。",
    column: {
      cases: "问题数",
    },
  },
  projectSettings: {
    title: "{name} · 设置",
    description: "用就绪清单核对开跑所需配置；配齐后即可在「运行」里开测。",
    dirty: "有未保存修改",
    guard: "项目默认设置有未保存修改，离开后将丢失。",
    saved: "项目默认设置已保存",
    saveFailed: "保存失败：{message}",
    refreshed: "设置数据已刷新",
    loadingTitle: "正在加载项目设置",
    loadingDescription: "正在读取默认评测方案与就绪状态。",
    openFailed: "项目无法打开",
    backToProjects: "返回项目列表",
    lastSync: "上次同步 {time}",
    readinessTitle: "开跑就绪清单",
    readinessSummary: "{ready}/{total} 项就绪。预检失败时可从新建测试任务直接打开对应设置。",
    configure: "去配置",
    defaultsTitle: "项目默认",
    defaultsDescription: "负责人与默认评测方案以已保存 API 为准；未设置时不会伪选中任何配置。",
    cancelChanges: "取消更改",
    owner: "负责人（Owner）",
    ownerAria: "项目负责人",
    ownerPlaceholder: "姓名或邮箱",
    defaultProfile: "默认评测方案",
    noProfiles: "暂无配置",
    notSet: "未设置",
    profileSetHint: "保存后，总览与新建测试任务会同步预选此项。",
    profileUnsetHint: "当前为未设置：就绪清单会标黄，新建测试任务需手动选择或使用备用方案。",
    createProfileHintPrefix: "请先在 ",
    createProfileHintSuffix: " 中创建。",
    profilesUnavailable: "评测方案接口暂不可用：{message}",
    agentContext: "AI Engineer 上下文",
    agentContextNone: "不使用额外上下文",
    agentContextContract: "IM Production 页面 URL",
    agentContextHint: "创建评测时可选填 IM Production 页面 URL；评测平台仅传递包含查询参数的当前路由，由 IM Production 解析业务上下文。",
  },
  projects: {
    description: "项目是题库、测试集和运行记录的统一归属边界。",
    create: "新建项目",
    createDescription: "创建后可维护独立题库、测试集和运行记录。",
    createSubmit: "创建项目",
    createFirst: "创建第一个项目",
    closeCreateForm: "关闭新建项目表单",
    created: "项目「{name}」已创建",
    createFailed: "创建失败：{message}",
    createFailedTitle: "项目创建失败",
    checkInput: "请检查输入",
    search: "搜索项目",
    searchPlaceholder: "项目名称、标识或 Owner",
    loadingTitle: "正在加载项目",
    loadingDescription: "正在获取项目及其题库状态。",
    errorTitle: "项目加载失败",
    errorDescription: "暂时无法获取项目。",
    emptyTitle: "还没有项目",
    emptyDescription: "创建项目后，就可以新增问题并组织测试集。",
    noMatchTitle: "没有匹配的项目",
    noMatchDescription: "请调整搜索词或状态筛选。",
    tableLabel: "项目列表",
    unassigned: "未指定",
    open: "打开项目",
    status: {
      active: "启用",
      draft: "草稿",
      archived: "已归档",
    },
    field: {
      name: "项目名称",
      key: "唯一标识",
      type: "项目类型",
      defaultEnvironment: "默认环境",
      description: "业务说明",
    },
    column: {
      questions: "问题数",
      lastRun: "最近运行",
    },
  },
  overview: {
    title: "总览控制台",
    description: "查看项目、题库资产和最近评测运行状态。",
    loadingTitle: "正在加载工作台",
    loadingDescription: "正在汇总项目、测试集和运行记录。",
    errorTitle: "总览加载失败",
    errorDescription: "暂时无法获取数据。",
    storageErrorTitle: "数据存储连接异常",
    storageErrorDetail: "当前存储：{provider}；新建测试任务前必须先恢复连接。",
    checkSettings: "检查系统设置",
    failedRunsTitle: "有 {count} 个失败运行待处理",
    failedRunsDetail: "最近失败：{name}",
    viewFailedRuns: "查看失败运行",
    noProjectsTitle: "还没有活跃项目",
    noProjectsDetail: "项目是题库与测试集的归属边界，先建项目再准备测试集。",
    noSuitesTitle: "尚无测试集，无法开跑",
    noSuitesDetail: "评测以测试集为输入；先在项目测试集内创建测试集。",
    prepareSuites: "去准备测试集",
    activeRunsTitle: "有 {count} 个运行正在执行",
    activeRunsDetail: "可进入运行队列跟踪进度、停止或续跑。",
    trackActiveRuns: "跟踪执行中的运行",
    readyTitle: "资产就绪，可以新建测试任务",
    readyDetail: "项目与测试集齐备；选择项目即可开跑。",
    statusStripAria: "平台状态与下一步",
    failedRunsCount: "失败运行：{count}",
    activeRunsCount: "执行中：{count}",
    storageState: "数据存储：{state}",
    storageOk: "正常 · {provider}",
    storageError: "异常",
    connected: "已连接",
    quickLinksAria: "快捷入口",
    recentRuns: "最近运行",
    recentRunsHint: "所有评测运行的统一执行视图（创建时间倒序前 8 条）",
    viewAll: "查看全部",
    noRunsTitle: "暂无运行记录",
    noRunsDescription: "先选择测试集并创建一次评测。",
    health: "运行健康",
    healthHint: "数据存储与运行状态",
    running: "运行中",
    runningHint: "正在校验或执行",
    queued: "排队中",
    queuedHint: "等待 Worker 领取",
    storage: "数据存储",
    statusChecking: "状态检查",
    metric: {
      activeProjects: "活跃项目",
      activeProjectsHint: "题库和测试资产的归属边界",
      questions: "项目问题",
      questionsHint: "各项目有效问题合计（不含已归档）",
      suitesHint: "固定引用问题版本",
      active: "执行中",
      activeHint: "排队、校验、运行或整理中",
    },
    link: {
      projects: "打开项目并维护其题库与测试集",
      questionBank: "选择一个项目题库开始维护",
      suites: "查看固定版本的评测资产",
    },
    column: {
      time: "时间",
    },
  },
  failures: {
    title: "失败问题复核",
    runMissing: "运行记录不存在或已被删除",
    requestFailed: "请求失败",
    selectCase: "选择失败问题查看详情。",
    caseHeading: "问题 {caseId}",
    previousAria: "上一条失败问题",
    nextAria: "下一条失败问题",
    viewInRun: "在原运行中查看",
    viewTurnLogs: "查看该轮日志",
    retryIncomplete: "重试不完整结果",
    userQuestion: "用户问题",
    belowThreshold: "未达到及格线，需要复核回答与标准答案。",
    retryStarted: "问题 {caseId} 第 {index} 次回答已开始技术重试",
    retryFailed: "重试失败：{message}",
    downloadStarted: "失败明细下载已开始",
    downloadFailed: "下载失败：{message}",
    downloading: "下载中",
    downloadDetails: "下载失败明细",
    loading: "正在加载失败问题",
    loadFailed: "失败问题加载失败",
    retryConfirmTitle: "重试问题「{caseId}」第 {index} 次回答？",
    retryConfirmDescription: "将在当前运行中修复这一条不完整结果；原始失败记录继续归档保留。",
    retryConfirm: "确认重试",
    failedCount: "{count} 条未通过的重复回答",
    affectedCases: "涉及 {count} 个问题",
    runId: "运行 ID：{id}",
    minScore: "最低分",
    maxScore: "最高分",
    correctness: "正确性",
    incorrect: "错误",
    correct: "正确",
    filterSummary: "当前显示 {shown} / {total} 条未通过的重复回答，涉及 {cases} 个问题。默认按问题 ID 升序、同一问题按重复回答序号排列；分数区间仅用于二次筛选，不决定是否失败。",
    tableLabel: "失败问题列表",
    rowAria: "查看失败问题：{caseId}，第 {index} 次回答，已执行轮次平均分 {average}，完成 {done}/{total}，未通过",
    noFiltered: "当前筛选条件下没有失败问题。",
    paginationAria: "失败问题分页",
  },
  logsPage: {
    networkFailed: "网络连接失败，请检查服务是否可用后重试。",
    loadFailed: "日志加载失败",
    loading: "正在加载日志",
    backToFailures: "返回失败复核",
    backToRunList: "返回原运行列表",
    copied: "日志已复制",
    copyFailed: "复制失败：{message}",
    exportStarted: "日志导出已开始",
    exportFailed: "日志导出失败：{message}",
    exportFiltered: "导出当前筛选（{count} 条）· JSONL",
    exportAll: "导出全部日志（{count} 条）· JSONL",
    exportBlockedError: "日志加载失败，重新加载后才能导出",
    exportBlockedLoading: "日志加载完成后才能导出",
    exportBlockedEmpty: "当前条件下没有可导出的日志",
    exporting: "导出中",
    currentRun: "当前运行：",
    selectRunHint: "选择运行后查询日志",
    confirmingRun: "正在确认运行记录",
    confirmRunFailed: "无法确认运行记录",
    reloadRuns: "重新加载运行列表",
    linkExpired: "当前日志链接已经失效，请返回日志首页选择仍然存在的运行。",
    viewAvailableLogs: "查看可用日志",
    runsLoadFailed: "运行加载失败",
    noLogsTitle: "还没有运行日志",
    noLogsDescription: "完成一次评测后，AI Engineer 和 Judge 日志会出现在这里。",
    importSuite: "导入测试集",
    runSelect: "评测运行",
    noRuns: "暂无运行",
    caseIdPlaceholder: "例如 QUESTION-0001",
    runIndex: "运行遍次",
    dateFrom: "开始日期",
    dateTo: "结束日期",
    query: "查询",
    querying: "查询中",
    filtersDirty: "筛选条件已修改，按 Enter 或点击“查询”应用。",
    content: "日志内容",
    contentPlaceholder: "搜索日志内容",
    categoryGroup: "日志类型",
    rawLevel: "原始级别：{label}",
    rawLevelLabel: "原始级别",
    rawLevelTitle: "原始级别取值 {level}",
    clearRawLevel: "清除原始级别筛选",
    resetFilters: "重置筛选",
    dateRangeInvalid: "结束日期不能早于开始日期。",
    rowAria: "查看日志详情：{time}，{category}，{caseId}，{message}",
    noCaseId: "无问题 ID",
    noMessage: "无内容",
    noFiltered: "当前筛选条件下没有日志。",
    noLogsForRun: "这次运行还没有日志记录。",
    countRange: "第 {from}-{to} 条，共 {total} 条日志 · 默认按时间倒序，每页 {pageSize} 条",
    countEmpty: "共 0 条日志 · 默认按时间倒序，每页 {pageSize} 条",
    detailTitle: "日志详情",
    detailEmpty: "选择一条日志查看完整内容。",
    businessType: "业务类型",
    runIdLabel: "运行 ID",
    redactNote: "认证类字段已脱敏，「复制日志」复制的也是脱敏后的内容。",
    copyLog: "复制日志",
    viewRelatedCase: "查看关联问题",
    openRunResult: "打开运行结果",
    category: {
      error: "运行错误",
      finding: "评分结果",
      warning: "运行警告",
      info: "运行信息",
    },
    column: {
      source: "来源",
      message: "内容摘要",
    },
  },
  projectDetail: {
    loadingTitle: "正在加载项目",
    loadingDescription: "正在获取项目题库、测试集和运行信息。",
    missing: "项目不存在或暂时不可用。",
    noProfileTitle: "配置缺口：尚未保存默认评测方案",
    noProfileDetail: "总览与新建测试任务必须以已保存的默认配置为准；当前 API 为未配置。",
    saveDefaultProfile: "去保存默认评测方案",
    noBindingTitle: "尚未配置环境绑定",
    noBindingDetail: "配置 Stage × Endpoint × 租户后才能拉取 AI Engineer 开跑。",
    noBindingHint: "在「环境绑定」中按 Stage 配置平台地址、账号与租户。",
    noBindingReadOnly: "只读项目不能新增环境绑定。",
    configureFirstBinding: "配置第一个绑定",
    addBinding: "新增环境绑定",
    addBindingShort: "新增绑定",
    readyWithReportTitle: "项目可开跑；已有最近报告",
    latestReportDetail: "最近报告：{name}",
    viewLatestReport: "查看最近报告",
    readyTitle: "项目就绪，可以新建测试任务",
    readyDetail: "默认评测方案与环境绑定已就绪。",
    statusStripAria: "项目状态与下一步",
    defaultProfileLabel: "默认评测方案：",
    profileConfigured: "已配置 · {name}",
    notConfigured: "未配置",
    latestReportLabel: "最近报告：",
    none: "暂无",
    basicInfo: "项目基本信息",
    basicInfoHint: "配置真相与治理字段；重复入口已收敛到状态条与 KPI。",
    ownerHint: "项目维护责任人",
    assignOwner: "指定负责人",
    defaultProfileHint: "以已保存 API 字段为准",
    projectKey: "项目标识",
    projectKeyHint: "稳定的唯一标识",
    bindingSummary: "环境绑定摘要",
    bindingSummaryHint: "首屏仅保留这一处摘要；管理与新增请进入设置。",
    manage: "管理",
    bindingTableLabel: "项目环境绑定",
    metric: {
      questionsHint: "已启用问题（与题库「已启用」筛选一致）",
      runs: "运行记录",
      runsHint: "当前项目的历史评测",
      bindings: "环境绑定",
      bindingsHint: "Stage × Endpoint × 租户",
    },
    link: {
      content: "测试集为主；题库作为创建测试集原料",
      runs: "查看运行进度与历史运行",
      tasks: "创建评测并跟踪排队、执行与异常状态",
      results: "查看逐题输出、完整度、得分与通过情况",
      reports: "查看已完成评测的结论与明细",
      production: "筛选并同步真实会话，加工为 标准答案",
      questions: "新增、编辑、查看历史和跨项目复制",
      settings: "环境、评测方案、提示词与项目默认",
    },
    column: {
      connection: "连接",
      tenant: "租户",
      stage: "阶段",
    },
  },
  projectPrompts: {
    title: "{name} · 提示词",
    description: "维护本项目答案评分提示词；新建测试任务时可选用并临时编辑。",
    createTitle: "新建提示词",
    createSubmit: "创建提示词",
    editTitle: "编辑「{name}」",
    editorDescription: "开跑时选中的正文会快照进运行；这里维护的是可复用正文，不是评分规则本身。",
    unsaved: "未保存",
    clearDraft: "清空草稿",
    namePlaceholder: "例如：严格评分提示词",
    descriptionPlaceholder: "适用场景或维护说明（可选）",
    body: "正文",
    bodyPlaceholder: "写入 Judge 提示词全文",
    guard: "提示词有未保存修改，离开后将丢失。",
    created: "提示词「{name}」已创建",
    updated: "提示词「{name}」已更新",
    updateFailed: "更新失败：{message}",
    deleted: "提示词已删除",
    deleteFailed: "删除失败：{message}",
    refreshed: "提示词已刷新",
    loadingProject: "正在获取提示词库。",
    loading: "正在加载提示词",
    loadingHint: "首次打开时，若库为空，后端会自动生成一条系统默认正文。",
    unavailable: "提示词库暂不可用",
    emptyTitle: "还没有提示词",
    emptyDescription: "点刷新可触发系统默认生成，或直接新建一条。",
    total: "共 {count} 条",
    tableLabel: "项目提示词库",
    referencedByDefault: "被默认评测引用",
    notReferenced: "未被评测方案引用",
    noDescription: "无说明",
    readOnly: "只读",
    discardTitle: "放弃未保存的提示词修改？",
    discardDescription: "关闭编辑器会丢弃本次填写的名称、说明与正文，已保存的提示词不受影响。",
    discardConfirm: "丢弃修改",
    deleteTitle: "删除提示词「{name}」？",
    deleteTitleFallback: "删除提示词？",
    deleteDescription: "历史运行中的提示词快照不会被改写。若仍被评测方案引用，删除后对应配置将回退到系统默认。",
    deleteReferences: "当前被引用：{names}",
    deleteConfirm: "确认删除",
    column: {
      references: "引用",
      preview: "说明 / 正文预览",
      updated: "更新",
    },
  },
  queue: {
    description: "跨项目查看评测状态；可按项目筛选。日常请优先使用项目内「运行」。",
    projectDescription: "创建测试任务，并跟踪排队、执行、异常与重试状态。",
    dataUnavailable: "运行数据暂不可用",
    refreshing: "正在刷新",
    totalRuns: "共 {count} 个运行",
    totalTasks: "共 {count} 个测试任务",
    updatedSuffix: " · 已更新于 {time}",
    searchPlaceholder: "搜索运行名称、ID、AI Engineer 或环境",
    searchAria: "搜索运行",
    searchTaskPlaceholder: "搜索任务名称、ID、AI Engineer 或环境",
    searchTasksAria: "搜索测试任务",
    statusAll: "全部状态",
    statusFilterAria: "按运行状态筛选",
    projectFilterAria: "按项目筛选运行",
    allProjects: "全部项目",
    environmentFilterAria: "按环境筛选测试任务",
    allEnvironments: "全部环境",
    agentFilterAria: "按 AI Engineer 筛选测试任务",
    filterSummary: "筛选结果 {shown} / {total} 条",
    loadingTitle: "正在加载运行队列",
    loadingDescription: "正在获取最新运行状态，请稍候。",
    errorTitle: "运行队列加载失败",
    errorDescription: "暂时无法获取运行列表。",
    emptyTitle: "暂无评测运行",
    emptyDescription: "创建第一条评测运行后，可以在这里追踪执行进度并打开运行结果。",
    emptyProjectDescription: "当前项目还没有测试任务。",
    noMatchTitle: "没有匹配的运行",
    noMatchDescription: "请调整搜索关键词或状态筛选条件。",
    tableLabel: "运行队列",
    projectTableLabel: "项目测试任务",
    batch: "批次 {position}/{size} · {id}",
    progressAria: "运行进度 {name}",
    questionCompletedSummary: "正常完成 {completed}/{total} 个问题；未正常完成 {incomplete}",
    questionProcessedSummary: "已处理 {done}/{total} 个问题",
    questionCompletionUnknown: "共 {total} 个问题；暂无可靠的正常完成数量",
    viewDetail: "查看详情",
    viewDetailAria: "查看详情 {name}",
    actionAria: "{action} {name}",
    stop: "停止",
    stopping: "停止中",
    resume: "续跑",
    resuming: "续跑中",
    newRunDescription: "选择环境、AI Engineer、测试集与次数即可开跑。",
    column: {
      run: "测试任务",
      environment: "环境",
      agentContext: "AI Engineer 上下文",
      status: "状态",
      progress: "进度",
      cases: "完成问题",
      repeat: "重复",
    },
    confirm: {
      deleteTitle: "删除这个运行？",
      deleteDescription: "删除后将移除该运行的运行结果、报告和日志记录。此操作不可撤销。",
      stopTitle: "停止这个运行？",
      stopDescription: "正在执行的评测会被中止。停止后不能从当前运行续跑；如需再次执行，请进入运行详情选择「重新运行」。",
      resumeTitle: "续跑这个运行？",
      resumeDescription: "系统会从已保存进度继续执行，可能产生新的模型调用。",
      target: "运行：{name}",
      stop: "确认停止",
      resume: "确认续跑",
    },
  },
  connections: {
    title: "接入连接",
    description: "配置被测平台域名（如 https://tesaqaedev.tesa.cn/）、登录账号，并验证能否拉取租户。Judge 模型请到评测方案。",
    create: "新建连接",
    createFirst: "创建第一个连接",
    scope: "作用域",
    scopeFilterAria: "按作用域筛选连接",
    scopeWorkspace: "工作区共享",
    scopeProject: "项目私有",
    scopeProjectDefault: "项目私有（默认）",
    summary: "共 {count} 条",
    summaryTotal: " / 全部 {total} 条",
    loadingTitle: "正在加载连接",
    loadingDescription: "正在读取接入连接列表。",
    errorTitle: "连接列表暂不可用",
    emptyTitle: "还没有接入连接",
    emptyDescription: "先创建连接并填写平台域名与账号，才能在项目里拉租户 / AI Engineer 发起评测。",
    noMatchTitle: "没有符合筛选条件的连接",
    noMatchDescription: "当前筛选「{scope}」下没有连接，共有 {total} 条连接。",
    noPlatformUrl: "未配置域名",
    passwordConfigured: "已配置密码",
    notConfigured: "未配置",
    fetchTenants: "拉租户",
    goToEnv: "去配环境",
    legacyMappingTitle: "和旧「环境设置」的对应关系",
    legacyMappingBody: "域名 + 登录账号 → 本页接入连接；租户 / AI Engineer 绑定 → 项目「环境」；Judge 模型 → 评测方案。",
    createTitle: "新建接入连接",
    editTitle: "编辑连接 · {name}",
    createDescription: "填写平台地址与账号 →「连接并获取租户」验证 → 保存。随后在项目「环境」里选租户 / AI Engineer。",
    editDescription: "更新域名或账号后可重新拉取租户验证。密码留空表示不修改。",
    unsaved: "未保存",
    unsavedGuard: "接入连接表单有未保存修改，离开后将丢失。",
    revert: "取消更改",
    fetchingTenants: "正在获取租户…",
    connectAndFetch: "连接并获取租户",
    fetchWithSaved: "用已保存凭据拉租户",
    saveConnection: "保存连接",
    saveChanges: "保存更改",
    noChanges: "无变更",
    connectorType: "连接器类型",
    connectorTypeHelp: "对接的平台类型标识，默认 im_production。",
    owningProject: "所属项目",
    noProjects: "暂无可用项目",
    platformUrl: "平台域名 / 登录页 URL",
    platformUrlHelp: "可填站点根地址或 /login；系统会推导租户登录与 AI Engineer API。",
    username: "登录用户名",
    password: "登录密码",
    passwordSavedPlaceholder: "已保存；留空则不改",
    passwordPlaceholder: "请输入密码",
    discardTitle: "放弃未保存的连接修改？",
    discardDescription: "关闭编辑器会丢弃本次填写的名称、平台域名、账号与密码，已保存的连接不受影响。",
    keepEditing: "继续编辑",
    discardConfirm: "丢弃修改",
    createdToast: "连接「{name}」已创建。可到项目「环境」拉租户 / AI Engineer。",
    createFailed: "创建连接失败：{message}",
    updatedToast: "连接「{name}」已更新",
    updateFailed: "更新连接失败：{message}",
    tenantsFetched: "已拉取 {count} 个租户",
    fetchTenantsFailed: "拉取租户失败：{message}",
    draftTenantsFetched: "账号可用，已发现 {count} 个租户。请保存连接。",
    connectFailed: "连接并获取租户失败：{message}",
    tenantPreviewSaved: "租户预览（已保存连接）",
    tenantPreviewDraft: "租户预览（尚未保存）",
    tenantPreviewHint: "共 {count} 个。创建项目环境绑定时再选具体租户与 AI Engineer。",
    tenantPreviewLimit: "仅展示前 {count} 个…",
    column: {
      name: "名称",
      platform: "平台地址",
      account: "账号",
    },
  },
  targets: {
    pageTitle: "{name} · 环境",
    pageDescription: "为每个环境阶段绑定平台地址与租户。开评测时再选该租户下的 AI Engineer。",
    unsavedGuard: "环境绑定表单有未保存修改，离开后将丢失。",
    formNotOpen: "表单未打开",
    passwordRequired: "修改平台地址或账号后，请填写密码再拉取租户；或仅改名称时无需重拉",
    missingBinding: "缺少绑定",
    createdToast: "环境绑定「{name}」已创建",
    createFailed: "创建失败：{message}",
    updatedToast: "环境绑定「{name}」已更新",
    updateFailed: "更新失败：{message}",
    defaultSetToast: "已将「{name}」设为默认环境",
    defaultFailed: "设置默认失败：{message}",
    disabledToast: "环境绑定已停用",
    disableFailed: "停用失败：{message}",
    deletedToast: "环境绑定已删除",
    deletedDisabledToast: "环境绑定已移除（仍有历史运行，已停用）",
    deleteFailed: "删除失败：{message}",
    readConnectionFailed: "读取接入配置失败：{message}",
    selectTenantFirst: "请先拉取并选择租户",
    loadingProject: "正在加载项目",
    loadingProjectDescription: "正在获取环境绑定。",
    projectUnavailable: "项目无法打开",
    backToProjects: "返回项目列表",
    lastSynced: "上次同步 {time}",
    refreshedToast: "环境绑定已刷新",
    addBinding: "新增绑定",
    loadingTitle: "正在加载环境绑定",
    loadingDescription: "按环境阶段展示平台地址与租户。",
    errorTitle: "环境绑定加载失败",
    emptyTitle: "尚未配置环境绑定",
    emptyReadOnly: "只读项目不能新增绑定。",
    emptyDescription: "在本项目添加环境阶段 + 平台地址 + 租户后，即可在新建测试任务里拉 AI Engineer。",
    createFirst: "配置第一个环境",
    stageCount: "{count} 条环境绑定",
    stageCountPaged: "本页 {shown} 条 / 共 {total} 条环境绑定",
    tableLabel: "{stage} 环境绑定",
    default: "默认",
    setDefault: "设为默认",
    disable: "停用",
    disableHint: "停用后不再出现在开跑选项",
    lastActiveDisableHint: "这是唯一启用中的绑定，停用后项目无法开跑；请先新增另一个绑定",
    lastActiveDeleteHint: "这是唯一启用中的绑定，不能删除；请先新增另一个绑定并设为默认，再删除本条",
    lastActiveTitle: "只剩一个启用中的绑定",
    lastActiveBody: "为避免项目无法开跑，唯一启用中的绑定不能停用或删除。请先新增另一个绑定并设为默认。",
    nextStepTitle: "下一步",
    nextStepPrefix: "到",
    nextStepSuffix: "：选环境 → 拉该租户 AI Engineer → 勾选后开跑。",
    createTitle: "新增环境绑定",
    editTitle: "编辑「{name}」",
    bindingFallback: "环境绑定",
    createDescription: "填写平台地址与账号 → 拉取租户 → 选定后保存。同一环境阶段仅保留一条启用中的绑定。",
    editDescription: "可轮换平台地址与凭证，无需先删除再建。密码留空表示不修改。",
    saveBinding: "保存环境绑定",
    stage: "环境阶段",
    displayName: "显示名称",
    displayNamePlaceholder: "例如：开发 · tesa",
    platformUrl: "平台地址 / 登录页 URL",
    setDefaultEnv: "设为默认环境",
    setDefaultEnvHelp: "新建测试任务优先预选此绑定",
    fetching: "拉取中…",
    connectAndFetch: "连接并拉取租户",
    selectTenant: "选择租户",
    chooseTenant: "请选择租户",
    keepTenant: "保持当前租户，或先拉取",
    fetchTenantsFirst: "请先拉取租户",
    discardTitle: "放弃未保存的环境绑定修改？",
    discardDescription: "关闭编辑器会丢弃本次填写的平台地址、账号、密码与租户选择，已保存的绑定不受影响。",
    deleteTitle: "删除环境绑定「{name}」？",
    deleteTitleFallback: "删除环境绑定？",
    deleteDescription: "删除后，无历史运行时会一并清理接入配置；若仍有历史运行，将改为停用并保留记录。",
    deleteDetails: "轮换密码或改域名请用「编辑」，不必删除。",
    column: {
      connection: "连接 / 接入配置",
      tenant: "租户",
    },
  },
  suitesPage: {
    title: "测试集库",
    description: "导入或选择测试集，查看问题内容、问题版本来源和标签快照。",
    globalDescription: "跨项目检索测试集并查看其问题、版本和可执行状态；导入和组集请进入所属项目。",
    globalEmptyDescription: "当前还没有可查看的测试集；请进入具体项目的测试集页面创建或导入。",
    actionFailed: "操作失败，请稍后重试",
    pickFile: "请选择 .xlsx Excel 测试集文件",
    pickProject: "请选择测试集所属项目",
    parsed: "测试集已解析，请处理冲突并确认写入",
    imported: "测试集导入完成",
    uploadFailed: "上传失败：{message}",
    savedToast: "测试集基本信息已保存",
    saveFailed: "保存测试集失败：{message}",
    idCopied: "测试集 ID 已复制",
    copyFailed: "复制失败，请手动复制测试集 ID",
    deletedToast: "测试集已删除",
    deleteFailed: "删除测试集失败：{message}",
    rebuiltSuiteName: "{name} · 当前版本",
    rebuiltToast: "已用题库当前版本创建新测试集「{name}」；原测试集使用的问题版本保持不变",
    rebuildFailed: "重建测试集失败：{message}",
    loadingTitle: "正在加载测试集",
    loadingDescription: "正在读取测试集列表和问题信息。",
    errorTitle: "测试集加载失败",
    importDialogTitle: "导入测试集",
    importDialogDescription: "选择 Excel 文件并解析；如有冲突，将前往项目题库复核后确认写入。",
    importToProject: "导入到项目",
    chooseProject: "请先选择项目",
    globalImportHint: "全局测试集页仅支持检索与跳转；导入前请先选择所属项目，或前往项目内测试集。",
    projectsLoadFailed: "项目列表加载失败，暂时无法导入。",
    backToContent: "返回测试集",
    backToSuites: "返回测试集库",
    downloadTemplate: "下载统一 Excel 模板",
    closeImportStatus: "关闭导入状态",
    missingTitle: "测试集不存在或已被删除",
    missingDescription: "链接中的测试集 ID「{id}」已经失效。请选择仍然存在的测试集，或导入新的 Excel 测试集。",
    emptyTitle: "还没有测试集",
    emptyDescription: "统一模板同时支持单轮和多轮。填写问题与对话轮次后导入，复核冲突并确认写入即可创建测试集。",
    allSuites: "全部测试集",
    listSummary: "{count} 个测试集 · 按{sort}",
    searchSuites: "搜索测试集",
    searchPlaceholder: "搜索名称或 ID",
    projectFilterAria: "按项目筛选测试集",
    caseCountSource: "{count} 条问题 · {source}",
    defaultProject: "默认项目",
    temporarySuite: "临时测试集",
    updatedAt: "更新于 {time}",
    noMatchingSuites: "没有匹配的测试集。",
    loadingCases: "正在加载问题",
    detailErrorTitle: "测试集详情加载失败",
    suiteName: "测试集名称",
    suiteNameRequired: "测试集名称不能为空",
    suiteType: "测试集类型",
    owningProject: "所属项目",
    projectImmutable: "项目归属不可直接修改",
    detailsUnsaved: "基本信息尚未保存",
    sourceLabel: "来源",
    cases: "问题",
    caseCount: "{count} 条",
    saveDetails: "保存基本信息",
    createRun: "创建评测",
    editDetails: "编辑信息",
    moreActions: "更多操作",
    copySuiteId: "复制测试集 ID",
    deleteSuite: "删除测试集",
    pinnedTitle: "本测试集使用创建时的问题版本。",
    legacyTitle: "兼容测试集的问题内容为只读。",
    staleHint: "题库中有 {count} 道题已有更新版本；采用当前版本将创建新测试集，不会静默改写本集。",
    pinnedHint: "需要修改时，请先在所属项目题库创建新版本，再创建使用新版本的测试集。",
    legacyHint: "为保证问题版本和历史评测可复现，请将内容导入或迁移到所属项目题库，再重新创建测试集。",
    rebuildWithCurrent: "采用当前版本重建",
    goToBank: "前往项目题库",
    migrateToBank: "迁移到项目题库",
    caseSearchPlaceholder: "搜索问题 ID 或问题",
    statusFilterAria: "状态筛选",
    refreshingCases: "正在刷新问题",
    caseSummary: "显示 {shown} / {total} · 可执行 {runnable} · 跳过 {skipped}",
    caseSummaryStale: " · 有新版本 {count}",
    caseSummarySort: " · 排序：问题在测试集中的原始顺序",
    notPinned: "无版本记录",
    upgradableSuffix: " · 有新版本",
    pinnedSuffix: " · 使用中",
    viewQuestion: "查看问题",
    readOnlyCase: "只读兼容问题",
    noMatchingCases: "没有匹配的问题",
    noCases: "当前测试集暂无问题",
    caseDetailTitle: "{id} · 问题详情",
    caseDetailPosition: "第 {index} / {total} 条（当前筛选结果）",
    previousCase: "上一条",
    nextCase: "下一条",
    caseDetailMeta: "使用版本：{version} · 状态：{status}",
    source: {
      questionBank: "项目题库创建",
      excel: "Excel 导入",
      pinned: "项目题库版本",
      manual: "手工维护",
    },
    caseStatus: {
      runnable: "可执行",
      questionDisabled: "问题已停用",
      questionArchived: "问题已归档",
      caseDisabled: "问题已停用",
      skipped: "已跳过",
    },
    column: {
      pinnedVersion: "使用版本",
      question: "问题",
      gtAnswer: "标准答案",
    },
    confirm: {
      leaveTitle: "放弃未保存的更改？",
      leaveDescription: "测试集有尚未保存的更改，离开后会丢失。",
      leaveConfirm: "放弃并离开",
      upgradeTitle: "采用题库当前版本重建测试集？",
      upgradeDescription: "将基于「{name}」创建新测试集，纳入 {count} 道题的题库当前版本。原测试集使用的问题版本不会被改写。",
      upgradeConfirm: "确认重建",
      rebuilding: "重建中…",
      deleteRunsTitle: "同时删除历史运行？",
      deleteRunsDescription: "「{name}」已有 {count}历史运行记录。继续将同时删除这些运行和对应报告，且不可撤销。",
      deleteRunsConfirm: "删除测试集与运行",
      runCount: "{count} 条",
      deleteTitle: "删除这个测试集？",
      deleteDescription: "删除「{name}」后将移除该测试集和其中的问题，此操作不可撤销。",
    },
  },
  profiles: {
    pageTitle: "{name} · 评测",
    pageDescription: "按项目配置评测体系、模型凭证、评分规则、运行默认和推荐提示词。",
    unsavedGuard: "评测方案有未保存修改，离开后将丢失。",
    noModel: "未配置模型",
    baseConfigured: "地址已配置",
    baseMissing: "缺少服务地址",
    keyConfigured: "密钥已配置",
    keyMissing: "缺少密钥",
    promptNamed: "提示词 {name}",
    promptSpecified: "已指定提示词",
    promptDefault: "默认提示词",
    defaultRepeat: "默认重复 {count}",
    createdToast: "评测方案「{name}」已创建",
    updatedToast: "评测方案已更新为 v{version}",
    deletedToast: "评测方案已删除",
    disabledInsteadToast: "配置已被引用，已改为停用",
    noChangeToast: "无变更，未生成新版本",
    blockDeleteReason: "「{name}」是项目默认评测方案。请先在「项目默认」中改选其他方案或清空默认，再删除。",
    blockDeleteTitle: "无法删除项目默认评测方案",
    gotIt: "知道了",
    goChangeDefault: "去改默认",
    loadingProjectDescription: "正在获取评测方案。",
    managePrompts: "维护提示词库",
    refreshedToast: "评测方案已刷新",
    create: "新建配置",
    createFirst: "创建第一个配置",
    promptsUnavailable: "项目提示词库暂不可用：{message}。仍可先用系统默认提示词创建配置。",
    loadingTitle: "正在加载评测方案",
    loadingDescription: "正在读取本项目的评测方案。",
    errorTitle: "评测方案暂不可用",
    emptyTitle: "还没有评测方案",
    emptyReadOnly: "只读项目不能新增配置。",
    emptyDescription: "创建后可在新建测试任务与项目设置中使用。",
    tableLabel: "评测方案",
    projectDefault: "项目默认",
    readOnlyShared: "只读共享",
    createTitle: "新建评测方案",
    editTitle: "编辑评测方案",
    createDescription: "分区填写评测骨架；推荐提示词来自本项目提示词库。",
    editDescription: "正在编辑「{name}」。有变更时保存会生成 v{version}；无变更不会升版。历史运行快照不变。",
    saveAndBump: "保存并升至 v{version}",
    discardTitle: "放弃未保存的评测方案修改？",
    discardDescription: "关闭编辑器会丢弃本次填写的模型、密钥、评分规则与运行默认，已保存的版本不受影响。",
    versionConfirmTitle: "确认生成评测方案 v{version}？",
    versionConfirmDescription: "保存后，后续新建测试任务会使用新版本默认值；历史运行快照不会被改写。",
    backToEdit: "返回修改",
    confirmBump: "确认升至 v{version}",
    deleteTitle: "删除评测方案「{name}」？",
    deleteTitleFallback: "删除评测方案？",
    deleteDescription: "若已被运行引用，将改为停用而非物理删除。项目默认配置不可直接删除。",
    deleteDetails: "当前版本 v{version}。删除前请确认没有依赖此配置的开跑路径。",
    namePlaceholder: "默认 Judge 配置",
    evaluatorKind: "评测体系",
    judgeBaseUrl: "评分模型服务地址",
    judgeBaseUrlHelp: "Judge 调用的 API Base URL，例如兼容 OpenAI 的网关地址。",
    judgeModel: "评分模型",
    judgeModelPlaceholder: "模型 ID",
    judgeApiKey: "评分模型密钥",
    judgeApiKeyPlaceholder: "写入后不可回显",
    judgeApiKeyHelp: "留空表示保持已保存密钥不变。",
    scoringRule: "打分规则",
    scoringRuleSingleTurn: "单轮问答评分",
    passThreshold: "及格线（0–100）",
    passThresholdHelp: "单次得分 ≥ 及格线视为通过。例：60 表示 60 分及格。",
    maxScore: "总分满分",
    maxScoreHelp: "评分量表上限，通常为 100。",
    consistencyThreshold: "一致性阈值（%）",
    consistencyThresholdHelp: "重复运行分数一致比例的下限；100 表示必须完全一致。",
    agentConcurrency: "AI Engineer 并发（路）",
    agentConcurrencyHelp: "同时向被测 AI Engineer 发起的请求数，1–50。调高会加快跑批，但更易触发对端限流。",
    agentRetry: "AI Engineer 失败重试（次）",
    agentRetryHelp: "单题失败后的额外重试次数，0–10。0 表示不重试，直接记为失败。",
    judgeConcurrency: "Judge 并发（路）",
    judgeConcurrencyHelp: "同时发起的评分模型调用数，1–100。调高会放大评分模型的额度与费用消耗。",
    judgeRetry: "Judge 失败重试（次）",
    judgeRetryHelp: "评分调用失败后的额外重试次数，0–10。每次重试都会再消耗一次模型调用。",
    repeatRuns: "默认重复运行次数",
    repeatRunsHelp: "每道题重复执行的次数（N）。",
    repeatMode: "重复执行方式",
    repeatModeParallel: "并发执行（推荐）",
    repeatModeSequential: "顺序执行",
    repeatConcurrency: "单题重复并发数",
    repeatConcurrencyHelp: "同一题的 N 次重复中，最多同时跑多少路。实际并发 ≤ min(重复次数, 本值)。",
    projectPrompt: "项目提示词",
    promptUnset: "不指定，运行时使用系统默认",
    promptsLoading: "正在读取项目提示词库…",
    promptsHelp: "列表来自本项目提示词库。可在「提示词」页维护，并查看被哪些评测方案引用。",
    evaluator: {
      toolCall: "工具调用",
      llmJudgeCurrent: "LLM Judge（当前）",
      ragasPlanned: "RAGAS（规划中）",
      toolCallPlanned: "工具调用正确性（规划中）",
      plannedHint: "规划中，暂不可用于开跑",
      help: "规划中的体系不可选；若误选将无法通过开跑预检。",
    },
    section: {
      basic: "基本信息 + 评测体系",
      basicHint: "评测方案定义怎么评；当前生产可执行体系为 LLM Judge。",
      model: "模型与凭证（LLM Judge）",
      modelHint: "仅用于评分模型调用；密钥保存后不会明文回显。",
      scoring: "评分规则",
      scoringHint: "决定单次结果如何判定通过，以及重复运行时的稳定性要求。",
      runtime: "运行默认",
      runtimeHint: "开跑页可继承这些默认值；高并发和高重试会放大外部调用压力。",
      prompt: "推荐默认提示词",
      promptHint: "仅 LLM Judge 使用；新建测试任务仍可临时选择或编辑，不会回写本配置。",
    },
    diff: {
      name: "名称：{before} → {after}",
      model: "模型：{before} → {after}",
      apiKey: "评分模型密钥：将更新",
      passThreshold: "及格线：{before} → {after}",
      consistency: "一致性阈值：{before} → {after}",
      repeatRuns: "默认重复次数：{before} → {after}",
      repeatConcurrency: "单题重复并发：{before} → {after}",
      generic: "已检测到字段变更（含地址、提示词或运行默认等）",
    },
    column: {
      kind: "体系",
      version: "版本",
      summary: "摘要",
    },
  },
  runDetailPage: {
    singleReport: {
      modularTitle: "模块化原型", modularHint: "五个模块独立计算、独立比较；缺失数据统一显示 -。", viewAria: "报告视图", single: "单份报告", history: "数据集历史", compare: "报告对比",
      questionRepeat: "{questions} 个问题 · 每个问题重复 {repeats} 次", snapshotTitle: "本次运行版本与提示词", snapshotHint: "报告保存待评测平台的运行时快照，当前配置变化不会改写历史报告。", frozen: "运行时快照", agentVersion: "AI Engineer 版本", platformVersion: "平台版本", promptVersion: "待评测平台提示词版本", repeatCount: "重复次数", snapshotValue: "运行时记录", unrecorded: "本次运行未记录", repeatUnit: "次 / 问题", runPrompt: "本次运行待评测平台提示词快照", currentPrompt: "当前待评测平台提示词", notInSnapshot: "非运行快照", currentPromptHint: "当前待评测平台提示词不属于本次运行快照，报告中不推断其内容。", targetPromptVersionHint: "待评测目标运行时记录", targetPromptMissingHint: "本次运行仅保存了待评测平台提示词版本，未保存提示词正文。",
      moduleAria: "报告核心指标与本次运行版本", modulePickerTitle: "核心指标", modulePickerHint: "选择指标查看对应分析模块；回答质量优先展示。", versions: "本次运行版本", versionsHint: "集中展示本报告实际使用的测试集、环境、AI Engineer、平台、提示词和重复配置。", viewDetails: "查看详情", aiEngineer: "评测 AI Engineer", environment: "运行环境", runtimeConfiguration: "本次运行配置", testSuite: "测试集", runId: "运行 ID", runtimeSnapshotId: "运行快照标识", questionConfiguration: "问题配置", overview: "本次运行版本", overviewHint: "仅展示本次运行的版本与配置快照。", notApplicable: "不适用", platform: "平台稳定性", stability: "回答稳定性", experience: "用户体验", cost: "成本", quality: "回答质量", module: { platform: "成功返回与评分有效性", stability: "重复回答一致性", experience: "响应耗时", cost: "Token 用量", quality: "有效回答质量" },
      platformHint: "只判断 AI Engineer 执行、回答返回与 Judge 评分链路，不把技术失败计入质量分。", failedQuestions: "{count} 个未完整问题", successRate: "问题正常完成率", executionErrors: "AI Engineer 执行报错", timeouts: "超时无回答", judgeValidRate: "Judge 有效评分率", definition: "口径", platformDefinition: "按正常完成问题数 / 总问题数统计；0 分但正常返回仍计入完成，任一重复回答未正常完成则该问题不完整。", qualityExclusion: "{count} 个未完整问题不计入回答质量均分。", failureReasons: "失败原因", failure: { agentError: "AI Engineer 执行过程报错", timeout: "超时且无最终回答", judgeFailure: "Judge 服务失败" },
      stabilityHint: "同一问题重复运行后，比较回答结论与内容是否保持一致。", stabilityNotTestedHint: "本报告每个问题只回答了一次，没有可用于比较的重复回答。", stabilityMinimumRepeatHint: "回答稳定性需要同一报告内每个问题至少重复回答 2 次；建议重复 3 次后再用于稳定性判断。", notTested: "未测试", repeatBadge: "每个问题 {count} 次", overallConsistency: "整体回答一致性", component: { conclusion: "核心结论一致", facts: "关键事实一致", citations: "引用一致", scoreVariance: "评分波动" }, fluctuatingQuestions: "波动问题数",
      experienceHint: "分开观察首字响应、完整回答和工具耗时；未采集项不作推断。", firstResponseP50: "首字响应 P50", firstResponseP95: "首字响应 P95", fullResponseP50: "完整回答 P50", fullResponseP95: "完整回答 P95", toolDuration: "工具调用耗时", answerDuration: "基于已记录的回答耗时", experienceDefinition: "完整回答耗时只使用 AI Engineer 回答阶段的已记录时长；不把排队或 Judge 时间混入。",
      costHint: "展示本次运行记录或确定性估算的 Token；金额按快照中的固定估算单价计算。", totalTokens: "总 Token", runTotal: "本次运行合计", avgTokens: "平均 Token", perCompleteAnswer: "每个完整回答", inputShare: "输入占比", outputShare: "输出占比", estimatedCost: "估算成本", estimatedCostNote: "按固定估算单价（USD）", tokenComposition: "输入与输出 Token 构成",
      qualityHint: "每个问题的质量分是七个质量维度中非空项的平均分；报告分是 39 个问题质量分的平均。", validQuestions: "{count} 个有效问题", overallScore: "回答质量均分", completeOnly: "不适用维度不计入分母", dimension: { correctness: "结论、事实与计算正确性", depth: "必要信息完整性与深度", conditions: "条件、前提与例外", scope: "主体、角色与接口范围", actionability: "可执行性与风险提示", evidence: "引用、证据与可追溯性", synthesis: "跨文件综合与冲突识别" }, scoringGate: "评分口径", scoringGateHint: "每个问题仅对非空维度求平均；不适用维度显示 - 且不计入分母，平台失败与未完整回答不自动记 0。", viewRelatedResults: "查看相关测试结果", platformDrilldownHint: "下钻到未完整结果，核对失败回答与运行证据。", stabilityDrilldownHint: "下钻到存在波动的问题，逐次比较完整回答。", experienceDrilldownHint: "在测试结果中查看每个问题的完整回答耗时。", costDrilldownHint: "在测试结果中查看每个问题的 Token 记录。", qualityDrilldownHint: "下钻到未达标问题，核对标准答案、回答和评分结论。", repeatAnswersCount: "{count} 次重复回答", table: { consistency: "一致性分", responseTime: "首字 / 完整回答", tokens: "Token", qualityScore: "问题质量分", judgeConclusion: "评分结论", returned: "已返回", stable: "稳定", attention: "需关注", noIssues: "-" },
    },
    agentContextTitle: "AI Engineer 上下文",
    agentContextContract: "当前路由：{route}",
    agentContextLegacy: "历史上下文 ID：{id}",
    agentContextSection: "页面模块：{section}",
    notFound: "运行记录不存在或已被删除",
    loadFailed: "运行详情加载失败",
    missingRunId: "运行 ID 缺失",
    backTo: "返回{target}",
    backToArrow: "← 返回{target}",
    loadingReport: "正在加载报告详情",
    loadingRun: "正在加载运行详情",
    openReportFailed: "无法打开报告详情",
    openRunFailed: "无法打开运行详情",
    errorHint: "{message}，请重试或返回{target}。",
    retryCreated: "已创建重试运行：{id}",
    retryFailed: "重新运行失败：{message}",
    agentRetryQueued: "AI Engineer 会话已进入重试队列",
    judgeRetryQueued: "Judge 评测已进入重试队列",
    stageRetryFailed: "阶段重试失败：{message}",
    caseRetryStarted: "问题 {caseId} 第 {index} 次回答已开始技术重试",
    caseRetryFailed: "问题重试失败：{message}",
    downloadStarted: "{kind} 下载已开始",
    downloadFailed: "下载失败：{message}",
    downloadDisabledHint: "运行尚未完成，暂时没有可下载的结果",
    downloading: "下载中",
    downloadJson: "下载 JSON",
    downloadExcel: "下载 Excel",
    viewFailures: "查看失败问题（{turns} 轮 / {cases}问题）",
    viewLogs: "查看日志",
    openEvaluationResult: "查看测试结果",
    openEvaluationReport: "查看测试报告",
    checkEnvSettings: "检查环境设置",
    creating: "创建中",
    rerun: "重新运行",
    reportDetail: "测试报告详情",
    runResult: "运行结果",
    taskDetail: "测试任务详情",
    resultDetail: "测试结果详情",
    repeatBadge: "运行 {count} 次",
    needsAttention: "需关注",
    runIdLabel: "运行 ID：{id}",
    viewTechnicalInfo: "查看技术信息",
    archivedIncompleteTitle: "已归档，但存在不完整结果",
    archivedIncompleteBody: "不完整结果不计入平均分与及格率，分数显示为 —；仅这些结果可以发起技术重试。",
    caseRetryRunningTitle: "正在重试不完整结果",
    caseRetryRunningBody: "重试完成后会自动更新当前运行的有效结果、汇总分数和下载文件，原始失败记录继续保留。",
    reportConclusion: "报告结论",
    resultSummary: "结果概览",
    resultAttentionHint: "结果中存在未达标、不完整或不一致的问题，可通过下方指标快速筛选。",
    resultHealthyHint: "当前结果未发现需关注异常，可继续逐题核对回答和评分证据。",
    attentionHint: "优先处理未达标、结果不完整与一致性问题，再决定修改问题、改 AI Engineer 或重跑。",
    noAttentionHint: "当前未见需关注异常；仍可下钻问题队列核对回答。",
    viewCaseQueue: "查看问题队列",
    clickSkipped: "点击查看被跳过问题",
    clickDrilldown: "点击下钻问题",
    runProgress: "运行进度",
    stageLabel: "阶段：{stage}",
    elapsed: "已用时 {duration}",
    stepsDone: "已完成 {done} / {total} 个执行步骤",
    currentCase: " · 当前问题 {caseId}",
    notStartedTitle: "运行尚未开始执行",
    notStartedBody: "启动前配置校验失败，计划的 {total} 个执行步骤均未调用 AI Engineer 或 Judge。请先修复环境设置，再重新创建评测。",
    noResultTitle: "尚未产生测试结果",
    noResultBody: "任务未通过启动前校验，因此没有可供分析的问题结果。请返回测试任务修复配置后重新运行。",
    resultMetrics: "测试结果指标",
    reportMetrics: "报告指标",
    runMetrics: "运行指标",
    reportViews: "报告详情视图",
    runViews: "运行详情视图",
    sourceLinks: "来源与跳转",
    runInfo: "运行信息",
    taskExecutionInfo: "运行配置",
    taskExecutionHint: "集中展示本次任务实际使用的评测对象、问题范围、执行快照和评分配置。",
    taskConfigTarget: "评测对象",
    taskConfigExecution: "执行快照",
    taskConfigEvaluation: "评分配置",
    taskCompletedTitle: "测试任务已完成",
    taskCompletedHint: "执行监控已经结束，可以继续核对逐题结果或查看聚合分析。",
    viewSkipped: "查看被跳过的 {count} 道问题",
    sourceDeepLinks: "报告来源入口",
    openSuite: "打开测试集",
    openBank: "打开题库",
    openRun: "查看原运行",
    openAttentionQueue: "进入异常问题队列",
    judgePromptBody: "评估提示词正文",
    judgePromptTitle: "本次评估提示词",
    promptBody: "提示词正文",
    viewPromptBody: "查看提示词正文",
    opsSecondary: "运维动作（次要）",
    stageRetry: "流程重试",
    multiTurnRetryTitle: "多轮运行需整体重试",
    multiTurnRetryBody: "为保持同一 Session 中的完整对话状态，不能单独重试 AI Engineer 或 Judge。请使用页面顶部“重新运行”，或在结果明细中对整个问题执行“重试”。",
    retryAgent: "重试 AI Engineer 会话",
    retryJudge: "重试 Judge 评测",
    skippedTitle: "本次跳过的问题",
    skippedDescription: "执行清单在创建运行时冻结，其中 {count} 道问题未执行，因此不会出现在问题队列里。",
    skippedCloseLabel: "关闭跳过问题列表",
    skippedNoDetail: "本次快照未逐条记录跳过明细，可在概述的跳过统计中查看数量。",
    confirmRetry: "确认重试",
    validation: {
      judgeTitle: "Judge 服务暂不可用",
      judgeDescription: "请检查当前运行环境的 Judge 地址、模型和认证信息，验证可用后再重新创建评测。",
      agentTitle: "AI Engineer 连接校验未通过",
      agentDescription: "请检查当前运行环境的 AI Engineer 地址、登录凭据和租户配置，连接成功后再重新创建评测。",
      genericTitle: "启动前配置校验未通过",
      genericDescription: "请检查当前运行环境配置，验证通过后再重新创建评测。",
    },
    confirm: {
      fullTitle: "重新运行这个运行？",
      fullDescription: "将按当前测试集重新创建完整运行，运行 {count} 次，可能产生新的模型调用。",
      agentTitle: "重试 AI Engineer 会话？",
      judgeTitle: "重试 Judge 评测？",
      stageDescription: "运行：{name}。系统将重新调用当前阶段，可能产生新的模型调用。",
      caseTitle: "重试问题「{caseId}」第 {index} 次回答？",
      caseDescription: "将在当前运行中修复这一条不完整结果并重新计算汇总；原始失败记录会继续归档保留。",
    },
    tab: {
      conclusion: "结论概述",
      overview: "结果概述",
      caseQueue: "问题队列",
      results: "结果明细",
    },
    metric: {
      avgScore: "问题执行平均分",
      scoreValue: "{score} 分",
      avgScoreHint: "只统计完整执行的问题；不完整结果不参与平均分",
      passRate: "及格率",
      passThreshold: "及格线 {threshold} 分",
      failedCases: "聚合未达标问题",
      failedCasesHint: "共 {count} 个问题 · 点击筛选",
      completionRate: "问题完成率",
      completionHint: "正常完成 {done} / {total} 个问题 · 可筛不完整",
      consistency: "一致性",
      consistencyHint: "逐题一致性分均值 · 点击筛选",
    },
    fact: {
      envAgent: "环境 / AI Engineer",
      environment: "运行环境",
      aiEngineer: "AI Engineer",
      agentContext: "AI Engineer 上下文",
      noAgentContext: "未配置额外上下文",
      elapsedActive: "评测运行已用时",
      elapsedDone: "评测运行执行时间",
      elapsedActiveHint: "运行开始至当前时刻，运行中持续累计",
      elapsedDoneHint: "运行开始至结果完成",
      completedQuestions: "完成问题",
      completedQuestionsHint: "0 分但正常返回仍计入；未正常完成才扣除",
      repeatCount: "重复配置",
      repeatCountValue: "每个问题 {count} 次",
      repeatCountHint: "创建任务时冻结",
      currentStage: "当前阶段",
      currentStageHint: "来自任务执行状态，不推断未记录阶段",
      agentTotal: "AI Engineer 执行总耗时",
      agentSamples: "{count} 次有效执行累计",
      agentAvg: "AI Engineer 执行平均耗时",
      agentAvgHint: "按有效执行次数计算",
      tokenUsage: "Token 用量",
      noRecord: "暂无记录",
      tokens: "{value} tokens",
      tokenMissingHint: "本次运行未回传模型用量，需后端在运行汇总中记录",
      tokenSplit: "输入 {prompt} · 输出 {completion}",
      tokenCombined: "Judge 与 AI Engineer 调用合计",
      estimatedCost: "预估成本",
      costMissingHint: "后端未回传成本估算",
      costHint: "按模型单价估算，仅供参考",
      runnableQuestions: "本次问题范围",
      runnableCount: "{runnable} / {total} 个问题",
      frozenHint: "创建运行时冻结，不跟随题库后续状态变化",
      skippedByQuestion: "因问题停用跳过",
      archivedExtra: "另有 {count} 条问题已归档",
      questionDisabled: "问题停用",
      skippedBySuite: "因测试集停用跳过",
      suiteItemDisabled: "测试集问题在创建运行时已停用",
      judgePrompt: "评估提示词",
    },
    dimension: {
      title: "分类准确率",
      description: "按问题主分类统计执行轮次通过率，同时展示问题数量，避免小样本百分比误导。",
      count: "{count} 个分类",
      empty: "当前测试集未标注主分类",
      emptyHint: "请在题库中为问题设置“查数据、查知识、混合场景”等问题类型标签，后续运行会自动生成分类指标。",
      questions: "问题",
      passed: "通过",
      failed: "未达标",
      avgScore: "平均分",
    },
    issues: {
      title: "AI Engineer 异常现象",
      caseCount: "{count} 个问题",
      pendingReview: "待复核",
      empty: "暂无与用户问题直接相关的 AI Engineer 异常。",
    },
    skipped: {
      index: "序号",
      reason: "跳过原因",
      question: "问题",
    },
  },
  results: {
    title: "测试结果",
    description: "查看已经产生的逐题输出、完整度、得分与通过情况；分析结论请进入报告。",
    total: "共 {count} 份结果",
    searchAria: "搜索测试结果",
    searchPlaceholder: "搜索任务、ID、AI Engineer 或测试集",
    filterSummary: "筛选结果 {count} 份",
    completenessFilterAria: "按结果完整性筛选",
    allCompleteness: "全部完整性",
    complete: "结果完整",
    incomplete: "结果不完整",
    suiteFilterAria: "按测试集筛选测试结果",
    agentFilterAria: "按 AI Engineer 筛选测试结果",
    loadingTitle: "正在加载测试结果",
    loadingDescription: "正在读取当前项目已经产生输出的测试任务。",
    errorTitle: "测试结果加载失败",
    errorDescription: "暂时无法读取结果，请重试。",
    noMatchTitle: "没有匹配的测试结果",
    noMatchDescription: "请调整搜索条件。",
    emptyTitle: "暂无测试结果",
    emptyDescription: "创建并执行测试任务后，结果会显示在这里。",
    tableLabel: "项目测试结果",
    column: { task: "测试任务", status: "状态", score: "平均分", passRate: "通过率", questions: "问题 / 重复", finished: "完成时间" },
    questionCount: "{count} / {repeats}",
    viewResult: "查看结果",
  },
  reports: {
    title: "全部测试报告",
    projectTitle: "测试报告",
    description: "全局检索入口。日常排查请优先进入具体项目的「报告」；此处适合跨项目查找。",
    projectDescription: "解读评测质量、定位异常问题，并决定下一步修改问题 / 重跑。",
    dataUnavailable: "报告数据暂不可用",
    total: "共 {count} 份报告",
    goToProjects: "前往项目",
    exportFiltered: "导出当前筛选",
    exportEmpty: "当前筛选没有可导出的报告",
    exported: "已导出当前筛选 {count} 份报告",
    viewReport: "查看报告",
    closeCompareNotice: "关闭比较链接提示",
    compareNotice: "链接中的 {count} 个比较运行已不存在或尚未生成可用报告，已从比较中移除。",
    searchPlaceholder: "搜索报告名或运行 ID",
    searchAria: "搜索报告",
    projectFilterAria: "按项目筛选报告",
    agentFilterAria: "按 AI Engineer 筛选",
    allAgents: "全部 AI Engineer",
    suiteFilterAria: "按测试集筛选",
    allSuites: "全部测试集",
    qualityFilterAria: "按质量结果筛选",
    allQuality: "全部质量结果",
    qualityAttentionOption: "存在未达标、结果不完整或一致性不足",
    sortAria: "报告排序",
    filterSummary: "筛选结果 {shown} / {total} 份",
    compareGuideAria: "报告比较引导",
    compareUnavailable: "横向比较暂不可用",
    compareUnavailableHint: "当前仅 1 份报告。再跑一轮评测后即可勾选对比；也可先打开详情排查问题。",
    runAgain: "再跑一轮",
    compareAria: "报告比较",
    compareTitle: "横向比较（{count}/4）",
    compareNeedMore: "再选择一份报告即可比较；不足 2 份时请先再跑一轮。",
    compareVerifying: "正在核验问题、标准答案与 Prompt 快照…",
    compareIncomparable: "存在不可直接比较的配置：{fields}",
    compareSnapshotUnknown: "部分历史报告缺少问题或 Prompt 快照，只能谨慎参考",
    compareComparable: "问题、标准答案、Judge Prompt 与关键评测方案一致，可以直接比较",
    compareSummaryOnly: "已选择超过 2 份报告，仅展示横向摘要；请选择恰好 2 份以获得可信的版本结论和问题变化。",
    compareAnalyzeFailed: "比较分析暂时失败，请稍后重试；当前不会生成优化结论。",
    listSeparator: "、",
    swapBaseline: "交换基线与候选",
    clearSelection: "清空选择",
    metricColumn: "指标",
    envJudge: "环境 / Judge",
    turnPassRate: "执行轮次通过率",
    turnPassRateValue: "轮次通过率 {value}",
    repeatDuration: "重复 / 耗时",
    repeatTimes: "{count} 次",
    scoringBasis: "评测口径",
    passThresholdValue: "及格线 {value}",
    maxScoreValue: "满分 {value}",
    caseImproved: "提升问题",
    caseRegressed: "退化问题",
    caseSame: "持平问题",
    caseStatusChanged: "个通过状态变化",
    caseLevelChanges: "问题级变化",
    caseLevelHint: "第一份为基线，第二份为候选；默认优先展示退化。",
    onlyRegressed: "只看退化",
    allChanges: "全部变化",
    columnCase: "问题",
    baseline: "基线",
    candidate: "候选",
    change: "变化",
    statusChangedHint: "通过状态发生变化",
    noRegressed: "没有退化问题。",
    caseReview: "问题复核",
    caseReviewHint: "最新一次回答并排复核",
    judgeConclusion: "评分结论",
    comparisonVerdictLabel: "优化结论",
    scoreDelta: "平均分变化",
    passRateDelta: "通过率变化",
    stablePassDelta: "稳定通过率变化",
    evidenceStrength: "证据强度",
    percentagePoint: " 个百分点",
    comparisonVerdict: {
      stable_improvement: "稳定提升：平均分变化 {score}，通过率变化 {passRate} 个百分点",
      observed_improvement: "观察到提升：平均分变化 {score}，通过率变化 {passRate} 个百分点",
      no_clear_change: "暂未观察到明确变化",
      mixed_with_regressions: "整体有所改善，但出现了回归问题",
      regressed: "候选版本出现退化，需要优先复核回归问题",
      incomparable: "当前运行不满足正式比较条件",
    },
    comparisonVerdictName: {
      stable_improvement: "稳定提升",
      observed_improvement: "观察到提升",
      no_clear_change: "无明确变化",
      mixed_with_regressions: "改善但有回归",
      regressed: "出现退化",
      incomparable: "不可正式比较",
    },
    evidence: {
      low: "低",
      medium: "中",
      high: "高",
    },
    comparisonDifference: {
      different_project: "项目不同",
      different_logical_agent: "逻辑 AI Engineer 不同",
      different_suite: "测试集不同",
      different_suite_snapshot: "测试问题或标准答案快照不同",
      different_judge_model: "Judge 模型不同",
      different_judge_prompt: "Judge Prompt 不同",
      different_evaluation_profile: "评测方案快照不同",
      different_pass_threshold: "及格线不同",
      different_max_score: "满分不同",
      different_scoring_rule: "评分规则不同",
      different_stage_code: "环境不同",
      different_target_id: "运行目标不同",
      different_repeat_runs: "重复次数不同",
      different_repeat_execution_mode: "重复执行策略不同",
      different_case_coverage: "两次运行的问题覆盖范围不同",
      incomplete_case_coverage: "存在未完整执行的问题",
      run_ineligible: "存在未完成或不完整的运行",
      same_environment: "环境对比选择了相同环境",
    },
    transition: {
      fixed: "失败转稳定通过",
      regressed: "稳定通过转失败",
      improved: "分数提升",
      regressed_score: "分数下降",
      same: "持平",
      stabilized: "由波动转稳定",
      destabilized: "由稳定转波动",
      incomparable: "结果不完整",
      baseline_only: "仅基线存在",
      candidate_only: "仅候选存在",
    },
    loadingTitle: "正在加载报告",
    loadingDescription: "正在获取已完成的测试结果，请稍候。",
    errorTitle: "报告加载失败",
    errorDescription: "暂时无法获取报告列表。",
    emptyTitle: "还没有可查看的报告",
    emptyPending: "有 {count} 个运行仍在执行或整理中，完成后会自动出现在这里。",
    emptyDescription: "评测运行完成后，报告会自动出现在这里。",
    viewActiveRuns: "查看执行中的运行",
    importSuite: "导入测试集",
    noMatchTitle: "没有匹配的报告",
    noMatchDescription: "请调整搜索关键词或筛选条件。",
    clearAllFilters: "清除全部筛选",
    selectCompare: "选择比较",
    selectCompareAria: "选择比较 {title}",
    compareNeedTwo: "至少需要 2 份报告才能比较",
    runIdMeta: "运行 ID · {id}",
    finishedMeta: "完成 · {time}",
    filterByAgentTitle: "只看 AI Engineer {agent} 的报告",
    consistencyMeta: "含一致性 {value}%",
    durationTitle: "耗时 {duration}",
    incomplete: {
      cases: "结果不完整 · {count} 个问题",
      runError: "结果不完整 · 运行报错",
      systemErrors: "结果不完整 · {count} 处执行错误",
      executed: "结果不完整 · 已执行 {executed}/{expected} 遍次",
      missingScore: "结果不完整 · 缺少评分",
    },
    quality: {
      unknown: "缺少可判定结果",
      gateFailed: "质量门禁未通过",
      failedCases: "{count} 个聚合问题未达标",
      incompleteTitle: "{label}（与报告详情「结果不完整」同口径）",
      consistencyLow: "一致性不足 {value}%",
      attention: "存在需关注项",
      attentionTitle: "存在需关注项（含一致性 / 结果完整性）",
      passed: "全部达标",
      passedTitle: "全部聚合问题达标",
    },
    cases: {
      executedSkipped: "{executed} 执行 · {skipped} 跳过",
      executedSkippedTitle: "可执行 {executed} 条，跳过 {skipped} 条{repeats}。悬停可查看跳过原因见报告详情。",
      runnable: "{executed} / {source}",
      runnableTitle: "本报告包含 {executed} / {source} 个问题{repeats}",
      aggregate: "{count} 个问题 · 重复 {repeats} 次",
      aggregateTitle: "聚合问题 {count} 个；重复运行 {repeats} 次",
      repeatSuffix: "；重复运行 {count} 次",
    },
    compareField: {
      suite: "测试集",
      judgeModel: "Judge 模型",
      passThreshold: "及格线",
      maxScore: "满分",
      scoringRule: "评分规则",
      contentSnapshot: "测试问题/标准答案快照",
      promptSnapshot: "Judge Prompt 快照",
    },
    sort: {
      finished: "按完成时间倒序（默认）",
      score: "按问题执行平均分（高到低）",
      passRate: "按执行轮次通过率（高到低）",
      failedCases: "按未达标问题最少",
      duration: "按耗时（长到短）",
    },
    csv: {
      title: "报告标题",
      runId: "运行ID",
      passRate: "轮次通过率",
      cases: "问题说明",
      finishedAt: "完成时间",
    },
    column: {
      report: "报告",
      quality: "质量结果",
      score: "问题均分 / 通过率",
      completedQuestions: "完成问题",
      finished: "执行完成 / 耗时",
    },
  },
  newRun: {
    pickerDescription: "保持当前页面不变，按「项目 → 环境 → AI Engineer → 测试集 → 评测方案」完成配置。",
    loadingProjects: "正在加载项目…",
    projectsLoadFailed: "项目列表加载失败：{message}",
    noRunnableProjects: "没有可发起评测的项目。历史容器「default」禁止新建 Run。",
    goToProjectAdmin: "前往项目管理",
    selectProject: "选择项目",
    selectProjectFirst: "请选择项目后继续",
    projectStepHint: "选择项目后，系统只加载该项目下的环境和测试资产。",
    projectLockedHint: "已经填写后续配置。如需切换项目，请先取消本次创建并重新打开。",
    pageTitle: "{name} · 新建测试任务",
    pageDescription: "选择环境、AI Engineer、测试集与次数即可开跑；评测方案齐全时主路径只显示摘要。",
    unnamedEnv: "未命名环境",
    defaultProjectNotice: "历史容器项目 default 禁止发起新 Run，请选择其他项目。",
    defaultProjectBlocked: "default 项目仅作历史数据容器，禁止发起新 Run。",
    suiteMissingNotice: "链接中的测试集已不存在或不属于本项目，请重新选择。",
    missing: {
      model: "模型",
      baseUrl: "服务地址",
      apiKey: "密钥",
      scoringRule: "评分规则",
      passThreshold: "及格线",
      maxScore: "满分",
      template: "评测方案缺少 {field}",
    },
    blocker: {
      selectProfile: "请选择评测方案",
      defaultProject: "default 项目禁止新建测试任务",
      selectBinding: "请选择环境绑定",
      loadingAgents: "正在加载当前环境的 AI Engineer…",
      agentsFailed: "AI Engineer 列表加载失败：{message}",
      selectAgent: "请至少选择一个 AI Engineer",
      noAgents: "当前环境未发现可用 AI Engineer",
      suiteAllSkipped: "该测试集没有可执行问题（{count} 条将被跳过）",
      selectSuite: "请选择至少包含一个可执行问题的本项目测试集",
      profileIncomplete: "{issues}，请先补全评测方案",
      customPrompt: "请填写本次自定义 Judge 提示词",
      libraryPrompt: "请选择项目提示词或填写临时提示词正文",
      repeatRange: "重复次数必须是 1–{max} 的整数",
      nameRequired: "高调用量运行必须填写运行名称",
      storageUnknown: "当前无法确认数据存储连接",
      settingsUnavailable: "环境绑定或评测方案暂不可用",
      validateAgentContext: "请先验证填写的 AI Engineer 页面 URL",
    },
    fix: {
      configureBinding: "去配置环境绑定",
      checkBinding: "检查环境绑定",
      prepareSuite: "去准备测试集",
      goToProfiles: "前往评测方案",
      completeProfile: "前往补全评测方案",
      openSettings: "打开项目设置",
    },
    hint: {
      retryInAgentArea: "可在 AI Engineer 区点击重试",
      pickAgents: "在列表中勾选要测的 AI Engineer",
      switchEnv: "切换环境或检查租户配置",
    },
    evalHint: {
      usingDefault: "已使用项目默认评测方案；需要时可更改。",
      manual: "当前为手动选择的评测方案；需要时可更改。",
      fallback: "未设项目默认评测方案，当前使用备用方案；需要时可更改。",
      noDefault: "项目默认评测方案未设置，请先选择评测方案。",
      generic: "评测方案与答案评分提示词决定怎么评。",
    },
    summary: {
      project: "项目：{name}",
      suite: "测试集：{name}",
      runnable: "可执行问题：{count} 个",
      skipped: "将跳过：{count} 个（问题停用 {disabled} 个，问题归档 {archived} 个，测试集停用 {suiteDisabled} 个）",
      repeat: "重复次数：{count}",
      environment: "环境：{name}",
      contractContext: "IM Production 当前路由：{route}",
      contractContextEmpty: "AI Engineer 上下文：未填写（可选）",
      contractContextDisabled: "AI Engineer 上下文：项目未启用",
      profile: "评测方案：{name}",
      prompt: "Judge 提示词：{name}{snapshot}",
      promptLibrary: "项目库",
      promptSnapshot: "（含临时正文快照）",
      promptNotApplicable: "Judge 提示词：非 LLM Judge，不适用",
      agents: "AI Engineer（{count}）：{names}",
      estimatedCalls: "预计调用：{count} 次 AI Engineer + 同等次数 Judge",
    },
    promptSaved: "已另存为项目提示词「{name}」",
    agentContextUrl: "IM Production 页面 URL（可选）",
    agentContextPlaceholder: "例如 https://im.example.com/contracts/123/clauses?clauseId=456",
    agentContextOptionalHint: "验证后保存并传递 pathname 与完整 query；业务实体和页面状态由 IM Production 解析。请勿填写含 Token、签名或凭据的 URL。",
    validateAgentContext: "验证",
    agentContextResolved: "已识别当前路由：{route}",
    promptSaveFailed: "另存为提示词失败：{message}",
    promptBodyRequired: "请先填写可保存的提示词正文",
    promptNameRequired: "请填写提示词名称",
    batchCreated: "评测批次已创建：{id}",
    runCreated: "评测运行已创建：{id}",
    partialTitle: "部分目标预检未通过",
    partialDescription: "尚未创建任何 Run。确认后将跳过不合格项，仅为可执行目标建运行。",
    partialEligible: "可执行 {count} 个目标。",
    partialNoEligible: "当前没有可执行目标。",
    partialConfirm: "跳过不合格项并创建",
    partialCancelled: "已取消部分创建",
    createFailed: "创建运行失败：{message}",
    suiteRefreshFailed: "测试集状态刷新失败，请重试",
    suiteChangedWithSkipped: "测试集状态已变化，当前没有可执行问题（{count} 条将被跳过）",
    suiteChanged: "测试集状态已变化，当前没有可执行问题",
    confirmSkipTitle: "确认跳过不合格目标？",
    confirmCreateTitle: "确认创建评测运行？",
    confirmSkipDescription: "部分目标预检未通过。确认后仅为可执行项创建 Run。",
    confirmCreateDescription: "请核对下列配置后再开跑。",
    start: "开始评测",
    leaveTitle: "离开新建测试任务？",
    leaveDescription: "本次评测有未提交更改，离开后草稿会丢失。",
    leaveConfirm: "仍要离开",
    discardTitle: "放弃当前新建测试任务？",
    discardDescription: "未提交草稿将丢失。",
    discardConfirm: "放弃草稿",
    customThisRun: "本次自定义",
    notSelected: "未选择",
    backToRuns: "返回测试任务",
    step1Title: "在哪测 · 测谁",
    step1Hint: "切换环境后自动加载该租户 AI Engineer，再多选本次对象。",
    step2Title: "测什么 · 测几遍",
    step2Hint: "选择本项目测试集，并确认重复次数。",
    envBinding: "环境绑定",
    configureBindingFirst: "请先配置环境绑定",
    bindingUnset: "未设置（请选择环境绑定）",
    agentsLabel: "AI Engineer（可多选）",
    agentsAutoLoad: "随环境「{name}」自动加载",
    agentsAfterEnv: "选择环境后自动加载该租户下的 AI Engineer",
    selectAll: "全选",
    selectAllVisible: "全选可见",
    clear: "清空",
    loadingAgents: "正在加载 AI Engineer…",
    loadFailed: "加载失败：{message}",
    agentSearchPlaceholder: "搜索名称、编码或角色",
    agentFilterAria: "筛选 AI Engineer",
    selectAgents: "选择 AI Engineer",
    noMatchingAgents: "无匹配 AI Engineer，请调整筛选",
    selectedCount: "已选 {selected} / {total} 个",
    willCreateRuns: "将创建 {count} 个独立 Run",
    willCreateOneRun: "将创建 1 个 Run",
    selectToStart: "勾选后开始评测",
    selectedNone: "已选 0 个",
    noSuites: "本项目暂无测试集",
    suiteUnset: "未设置（请选择测试集）",
    repeatRuns: "重复运行次数",
    runNameRequired: "本次运行名称（必填）",
    runNameOptional: "本次运行名称（可选）",
    runnableCases: "可执行问题",
    willSkip: "将跳过",
    estimatedCalls: "预计调用",
    estimatedCallsValue: "{count} 次 AI Engineer · {count} 次 Judge",
    pendingAgent: "待选 AI Engineer",
    selectedAgents: "已选 AI Engineer",
    collapseAdvanced: "收起高级",
    change: "更改",
    projectDefaultSuffix: "（项目默认）",
    fallbackSuffix: "（回退）",
    promptLabel: "提示词",
    editedSuffix: "（已临时编辑）",
    viewProjectDefault: "查看项目默认",
    setProjectDefault: "去设置项目默认",
    createProfileFirst: "请先创建评测方案",
    profileUnset: "未选择（项目默认未设置）",
    noDefaultProfileHint: "项目尚未设置默认评测方案，请在此选择，或",
    setDefaultLink: "去设置默认",
    periodSuffix: "。",
    profileBlockedPrefix: "评测方案尚不能开跑：{issues}。",
    goComplete: "前往补全",
    profileReady: "当前体系：{kind}；评测方案已满足开跑前置条件。",
    promptMode: "提示词模式",
    promptModeLibrary: "从库选择",
    promptLibraryUnavailable: "提示词库暂不可用",
    noPrompts: "本项目暂无提示词",
    promptUnset: "未设置（请选择提示词）",
    profileRecommended: "Profile 推荐：{name}",
    switchToCustomHint: "可切换到「本次自定义」继续开跑。",
    promptPreview: "全文预览 / 临时编辑",
    promptCustomBody: "本次自定义提示词正文",
    promptPreviewPlaceholder: "选择项目提示词后会填入全文，可在本次运行前临时修改。",
    promptCustomPlaceholder: "请输入本次 LLM Judge 使用的完整提示词。",
    promptSource: "来源：{name}",
    promptNotSelected: "尚未选择项目提示词",
    promptEditedHint: "；正文已临时编辑，不会回写库。",
    promptCustomHint: "自定义正文只随本次 Run 提交；需要复用时可另存为项目提示词。",
    saveAsProjectPrompt: "另存为项目提示词",
    promptNamePlaceholder: "例如：严格评分提示词",
    preflightTitle: "预检提交",
    preflightDescription: "服务端将探测 AI Engineer 是否仍存在。多 Target 若部分失败，会先提示，确认后可跳过不合格项。",
    canSubmit: "可以提交",
    cannotSubmit: "尚不能提交",
    serverPrecheck: "提交后由服务端完成硬预检",
    highVolumeConfirm: "高调用量确认：请输入运行名称「{name}」",
    highVolumeConfirmAria: "高调用量确认运行名称",
    cancelledDraftKept: "已取消，当前草稿仍保留",
    nameMismatch: "运行名称不匹配，运行尚未创建",
  },
  production: {
    pageDescription: "从 AI Engineer 同步系统记录，并将候选问题整理后发布到题库。",
    projectPrefix: "项目：{name}",
    tabsAria: "数据同步工作区",
    tabCandidates: "候选问题",
    tabSync: "同步配置",
    loadingWorkspace: "正在加载真实会话工作台",
    loadingWorkspaceHint: "正在确认项目与数据边界。",
    feedback: {
      all: "全部反馈",
      negative: "点踩",
      positive: "点赞",
      none: "无反馈",
    },
    duration: {
      seconds: "{value} 秒",
      minutes: "{value} 分钟",
      hours: "{value} 小时",
      days: "{value} 天",
    },
    totalDuration: "总耗时",
    elapsed: "已用 {value}",
    remaining: "预计剩余 {value}",
    remainingUnknown: "预计剩余：待估算",
    tenantReadFailed: "读取租户失败：{message}",
    agentReadFailed: "读取 AI Engineer 失败：{message}",
    capabilityOk: "History Read 能力检查通过",
    capabilityUnavailable: "History Read 能力当前不可用",
    capabilityFailed: "能力检查失败：{message}",
    previewFailed: "预览失败：{message}",
    syncJobCreated: "同步批次已创建，Worker 将按快照处理",
    syncJobCreateFailed: "创建同步批次失败：{message}",
    policyUpdated: "筛选策略已更新并生成新版本",
    policySaved: "筛选策略已保存",
    policySaveFailed: "保存策略失败：{message}",
    policyDisabled: "筛选策略已停用；历史同步批次不受影响",
    policyDisableFailed: "停用策略失败：{message}",
    filterConversation: "筛选会话",
    filterHint: "预览只读远端；确认后创建同步批次。",
    savedPolicy: "已保存策略",
    temporaryFilter: "临时筛选",
    temporaryFilterSuffix: " · 临时筛选",
    policyVersionSuffix: " · 策略 v{version}",
    connectionHint: "仅显示已启用且声明 history_read 的连接；选择后自动读取租户",
    connectionLabel: "连接",
    connectionAria: "连接",
    pleaseSelect: "请选择",
    selectConnectionFirst: "请先选择连接",
    loadingTenants: "正在读取租户…",
    readFailedRetry: "读取失败，可重试",
    selectTenant: "请选择租户",
    noTenants: "当前 Connection 下无租户",
    autoLoadFromConnection: "随 Connection 自动加载",
    retryTenants: "重试读取租户",
    agentOptional: "AI Engineer（可选）",
    selectTenantFirst: "请先选择租户",
    loadingAgents: "正在读取 AI Engineer…",
    autoLoadFromTenant: "随租户自动加载",
    retryAgents: "重试读取 AI Engineer",
    userFeedback: "用户反馈",
    startTime: "开始时间",
    endTime: "结束时间",
    scope: "归属范围",
    scopeMine: "我的",
    scopeShared: "共享",
    conversationStatus: "会话状态",
    allStatuses: "全部状态",
    statusActive: "活跃",
    statusRunning: "运行中",
    statusCompleted: "已完成",
    statusError: "错误",
    sourceDomain: "来源域",
    sourceDomainHint: "按用户邮箱域名筛选；填 web/api 通常为 0 条",
    blankMeansAll: "留空=全部",
    syncLimit: "同步上限",
    syncLimitAria: "同步数量",
    syncLimitHint: "本批最多新增的 Conversation 数",
    policyName: "策略名称",
    policyNamePlaceholder: "例如：近 7 天点踩会话",
    disable: "停用",
    updatePolicy: "更新策略",
    saveAsPolicy: "保存为策略",
    historyReadPrefix: "History Read：",
    notVerified: "尚未验证",
    checkCapability: "检查连接能力",
    verifyHistoryReadFirst: "请先验证当前 Connection 与租户的 History Read 能力",
    previewing: "预览中…",
    previewSync: "预览同步",
    noHistoryReadConnection: "当前项目没有可用的 history_read Connection。请先到项目设置中的 Connection 配置能力。",
    disablePolicyTitle: "停用筛选策略「{name}」？",
    unnamedPolicy: "未命名策略",
    disablePolicyDescription: "停用后该策略从下拉中消失，不能再用于创建新批次；已创建的同步批次与已生成的候选问题不受影响。",
    currentVersion: "当前版本：v{version}",
    disablePolicyIrreversible: "停用不可撤销，需要时请重新保存一条新策略。",
    disablePolicyConfirm: "确认停用策略",
    disabling: "停用中…",
    syncPreview: "同步预览",
    syncPreviewHint: "确认范围后再创建批次。",
    noPreview: "尚未生成预览",
    noPreviewHint: "选好筛选条件后点「预览同步」。",
    remoteEstimate: "远端估算",
    discovered: "已发现",
    alreadySynced: "已同步",
    evalSkip: "评测跳过",
    evalSkipTitle: "评测平台发起的 Conversation（含失败重试）",
    plannedNew: "计划新增",
    incompleteWindows: "有 {count} 个时间窗口无法完整枚举。",
    confirmSync: "确认同步 {count} 个 Conversation",
    jobStatusUpdated: "批次状态已更新",
    actionFailed: "操作失败：{message}",
    loadingJobs: "正在加载同步批次",
    loadingJobsHint: "正在读取批次状态与统计。",
    jobsLoadFailed: "同步批次无法加载",
    syncJobs: "同步记录",
    syncJobsHint: "创建时保存连接版本与筛选快照。默认按创建时间倒序，列头不提供筛选与排序。",
    jobsTableLabel: "Production 同步批次",
    column: {
      createdAt: "创建时间",
      processed: "处理",
      duration: "耗时",
      candidates: "候选问题",
      duplicate: "去重",
      failed: "失败",
      incompleteWindows: "不完整窗口",
      filterSummary: "筛选摘要",
      attempts: "尝试",
      error: "错误",
      candidate: "候选问题",
      turns: "轮次",
      lastEdited: "最近编辑",
      conversation: "会话",
    },
    processedTitle: "处理=会话数（成功/跳过+失败）· 远端命中 {remote} · 上限 {limit} · 候选问题见右侧",
    dateRange: "{from} 至 {to}",
    viewJobCandidates: "查看本批候选问题",
    collapse: "收起",
    retryFailed: "重试失败项",
    noJobs: "暂无同步批次",
    noJobsHint: "先在「高级 · 同步样本」中预览并确认一次同步。",
    loadingJobDetail: "正在加载批次详情",
    loadingJobDetailHint: "正在读取创建时的快照和逐条处理结果。",
    jobDetailFailed: "批次详情无法加载",
    jobHeading: "批次 {id}",
    createdBy: "创建者",
    startedAt: "开始",
    finishedAt: "完成",
    authTenant: "认证租户",
    viewSnapshot: "查看完整筛选快照与窗口状态",
    snapshotTitle: "批次 {id} 的筛选快照",
    snapshotDescription: "创建批次时保存的筛选条件与窗口状态，只读。",
    jobItemsTableLabel: "批次 Conversation 处理明细",
    noJobItems: "尚无 Conversation 明细",
    noJobItemsHint: "批次排队时属于正常状态；Worker 领取后会逐条记录。",
    cancelJobTitle: "取消同步批次 {id}？",
    cancelJobDescription: "Worker 会停止领取剩余 {count} 个 Conversation。已处理的部分与已生成的候选问题会保留。",
    cancelJobProcessed: "已处理 {done} / {target} 个 Conversation",
    cancelJobCandidates: "已生成候选问题 {count} 条",
    cancelJobIrreversible: "取消不可撤销；需要补齐剩余会话时要重新创建批次。",
    cancelJobConfirm: "确认取消批次",
    cancelling: "取消中…",
    bulk: {
      reject: "驳回",
      markDuplicate: "标记重复",
      addTags: "添加标签",
    },
    bulkPartial: "{label}完成 {ok} 条，{failed} 条失败",
    bulkDone: "已批量{label} {count} 条候选问题",
    bulkFailed: "批量操作失败：{message}",
    adoptPartial: "已采用 {ok} 条 Production 回答，{failed} 条跳过或失败",
    adoptDone: "已批量采用 {count} 条 Production 回答，请进入编辑器核对",
    unnamedCandidate: "未命名候选问题",
    loadingCandidates: "正在加载候选问题",
    loadingCandidatesHint: "正在读取候选问题与完整性状态。",
    candidatesLoadFailed: "候选问题无法加载",
    queueTitle: "待加工队列",
    metricsAria: "题库加工指标",
    pendingTitle: "待处理与编辑中候选问题",
    pending: "待加工",
    viewPending: "查看待加工候选问题",
    promotionRate: "入库率 {value}%",
    promoted: "已发布到题库",
    viewPromoted: "查看已发布的候选问题",
    jobCount: "{count} 个同步批次",
    loadingMetrics: "正在读取运营指标",
    synced: "已同步",
    viewJobs: "查看同步批次",
    medianDuration: "中位耗时",
    medianDurationTitle: "从同步到人工发布的中位耗时",
    searchCandidates: "搜索候选问题",
    searchPlaceholder: "来源问题、回答、反馈或草稿内容",
    candidateStatus: "候选问题状态",
    statusActiveQueue: "待加工 + 编辑中",
    editing: "编辑中",
    rejected: "已拒绝",
    duplicate: "重复",
    completeness: "完整性",
    completeReady: "可发布",
    missingQuestion: "问题不完整",
    missingGroundTruth: "缺少标准答案",
    attachmentUnresolved: "附件待处理",
    needsWork: "待完善",
    feedbackLabel: "反馈",
    feedbackType: "反馈类型",
    syncJob: "同步批次",
    allJobs: "全部批次",
    moreFilters: "更多筛选",
    moreFiltersCount: "（{count} 项已启用）",
    ratingLevel: "来源星级",
    allRatings: "全部星级",
    stars: "{count} 星",
    editedFilter: "是否编辑",
    editedFilterAria: "是否编辑过",
    clearSelection: "清除选择",
    editedYes: "已编辑",
    editedNo: "未编辑",
    editor: "编辑者",
    editorPlaceholder: "名称或账号",
    syncedFrom: "同步时间从",
    syncedTo: "同步时间至",
    sourceCreatedFrom: "真实会话时间从",
    sourceCreatedTo: "真实会话时间至",
    editedFrom: "最近编辑时间从",
    editedTo: "最近编辑时间至",
    bulkAria: "候选问题批量操作",
    selectedCount: "已选择 {count} 条",
    bulkScopeHint: "跨页保留：翻页不会清空选择，批量操作作用于全部已选条目（本页 {count} 条）。",
    tags: "标签",
    adopting: "采用中…",
    adoptBulk: "批量采用 Production 回答",
    duplicateTarget: "重复目标",
    duplicateTargetType: "重复目标类型",
    candidateId: "候选问题 ID",
    questionId: "正式问题 ID",
    duplicateTargetId: "重复目标 ID",
    duplicateTargetPlaceholder: "输入同项目目标 ID",
    bulkReject: "批量驳回",
    sortNote: "默认排序：点踩优先 → 短轮次 → 最近同步（固定顺序，列头不提供筛选与排序）",
    candidatesTableLabel: "真实会话候选问题",
    selectPageAria: "选择本页可编辑候选问题（已选条目跨页保留）",
    selectCandidateAria: "选择候选问题 {title}",
    turnCount: "{count} 轮",
    noCandidates: "没有符合条件的候选问题",
    noCandidatesFiltered: "当前筛选没有命中候选问题，可清除筛选后重试。",
    noCandidatesHint: "先同步真实会话，系统会自动生成候选问题。",
    rejectTitle: "驳回选中的 {count} 条候选问题？",
    rejectDescription: "驳回后这些候选问题将结束处理，不能再发布到题库；真实会话来源快照不会被删除。",
    affectedCount: "影响 {count} 条（跨页已选，其中本页 {onPage} 条）",
    rejectIrreversible: "驳回不可撤销，适合明显垃圾题或无法加工的会话。",
    rejectConfirm: "确认批量驳回",
    rejecting: "驳回中…",
    markDuplicateTitle: "将 {count} 条标记为重复？",
    markDuplicateDescription: "标记后这些候选问题将结束处理，不能发布到题库。",
    duplicateTargetSummary: "重复目标：{type} {id}",
    candidateLabel: "候选问题",
    questionLabel: "正式题",
    markDuplicateConfirm: "确认标记重复",
    marking: "标记中…",
  },
  candidate: {
    draftSaved: "候选问题草稿已保存 · {time}",
    saveFailed: "保存失败：{message}",
    splitDone: "已按轮次拆成独立候选问题，正在打开新题 {id}",
    splitFailed: "拆分失败：{message}",
    rejected: "候选问题已拒绝，已进入下一条（若有）",
    rejectFailed: "拒绝失败：{message}",
    markedDuplicate: "候选问题已标记为重复，已进入下一条（若有）",
    markDuplicateFailed: "标记重复失败：{message}",
    promoted: "候选问题已发布到题库",
    promoteFailed: "发布失败：{message}",
    noSourceAnswer: "该轮没有可填入的 Production 回答",
    filledTurn: "已填入第 {index} 轮 Production 回答",
    filledAll: "已填入 {count} 轮 Production 回答，请核对后保存",
    nothingToFill: "没有可填入的 Production 回答",
    terminalRejected: "该候选问题已拒绝，可继续下一条或返回队列。",
    terminalDuplicate: "该候选问题已标记为重复，可继续下一条或返回队列。",
    terminalPromoted: "该候选问题已发布到题库，可打开正式问题或继续下一条。",
    terminalGeneric: "该候选问题已结束处理。",
    needOneTurn: "请至少保留一轮问题与标准答案。",
    completeTurns: "请补全每一轮的问题与标准答案。",
    confirmAttachment: "请确认附件依赖已处理。",
    saveBeforePromote: "请先保存草稿后再发布到题库。",
    handleDuplicateCandidate: "请先标记重复或处理其他候选问题。",
    handleDuplicateQuestion: "请标记重复，或基于已有正式题创建新版本。",
    loadingTitle: "正在加载候选问题编辑器",
    loadingDescription: "正在读取不可变来源与候选草稿。",
    openFailed: "候选问题无法打开",
    backToCandidates: "返回候选问题",
    backToQueue: "返回候选问题队列",
    backToQueueShort: "回到队列",
    editorTitle: "候选问题编辑器",
    editorDescription: "左侧真实会话来源只读；右侧内容会作为正式问题与标准答案写入题库。",
    queuePosition: "队列 {index} / {total}",
    queueNavAria: "候选问题队列导航",
    previous: "上一条",
    next: "下一条",
    viewQuestion: "查看正式问题",
    terminalHint: "过期校验与编辑控件已清除。下一步操作在底部操作栏，优先继续队列，不必停在只读死页。",
    sourceTitle: "真实会话来源",
    sourceHint: "同步时保存的不可变会话快照",
    readOnly: "只读",
    agentPrefix: "AI Engineer：{name}",
    userPrefix: "用户：{name}",
    targetRound: "目标轮次：{value}",
    targetRoundHint: "目标轮次是同步时命中反馈的那一轮；多轮会话仍会展示完整对话供对照。",
    targetRoundNote: "（反馈命中轮；对话可含更多轮）",
    fillAllGt: "为全部轮次填入标准答案",
    fillAllHint: "填入后仍需人工核对并保存。",
    userRound: "用户 · 第 {round} 轮",
    productionAnswer: "真实会话回答（仅供参考，不是标准答案）",
    fillGt: "填入标准答案",
    attachmentCount: "包含 {count} 个附件引用",
    draftTitle: "候选问题草稿",
    draftHint: "重新定义问题并为每一轮补充标准答案。",
    splitByTurn: "按轮拆成单题",
    titleOptional: "标题（可选）",
    turnOrdinal: "第 {index} 轮",
    deleteTurn: "删除第 {index} 轮",
    question: "问题",
    groundTruth: "标准答案",
    gtPlaceholder: "填写该轮回答的标准答案",
    addTurn: "增加一轮",
    tags: "题库标签",
    note: "加工备注",
    attachmentHandled: "附件依赖已人工处理",
    attachmentHandledHint: "确认问题在离开原始附件后仍可独立理解，或已把必要信息写入问题。",
    duplicateFound: "发现完全相同的问题",
    officialQuestionPrefix: "正式题库：",
    candidatePrefix: "候选问题：",
    duplicateTerminalHint: "该候选问题已结束处理，此处仅保留重复关系用于追溯。",
    duplicateCandidateHint: "请先打开对比并标记重复，或处理其他候选问题后，再发布到题库。",
    duplicateQuestionHint: "请选择：标记为重复，或基于已有正式题创建新版本。",
    markDuplicate: "标记为重复",
    newVersionFromQuestion: "基于已有题创建新版本",
    terminalNoValidation: "已结束：无需再补全校验",
    validationPassed: "完整性校验通过",
    unsaved: "有未保存修改",
    savedAt: "已保存于 {time}",
    persistedAt: "已保存 · {time}",
    reject: "拒绝",
    saveDraft: "保存草稿",
    promoting: "发布中…",
    promote: "发布到题库",
    rejectTitle: "拒绝这条候选问题？",
    rejectDescription: "拒绝后候选问题将结束处理，不能再发布到题库；真实会话来源快照不会被删除。确认后将自动进入下一条（若有）。",
    candidateSummary: "候选问题：{name}",
    rejectHint: "适合垃圾题、无法加工或明显不可用的会话；拒绝不可撤销。",
    rejectConfirm: "确认拒绝",
    splitTitle: "按轮拆成独立候选问题？",
    splitDescription: "将基于当前多轮草稿生成新的独立候选问题副本（约 {count} 轮结构）。原题保留；拆分后请分别加工，避免两边内容完全相同却未处理重复。",
    splitHint: "拆分后会打开新候选问题；原题仍在队列中。",
    splitConfirm: "确认拆分",
    splitting: "拆分中…",
    duplicateTitle: "标记为重复？",
    duplicateDescription: "标记后当前候选问题将结束处理，不会发布到题库。确认后将自动进入下一条（若有）。",
    duplicateTargetQuestion: "重复目标（正式题）：{name}",
    duplicateTargetCandidate: "重复目标（候选问题）：{name}",
    duplicateCompareHint: "建议先点开重复项对比，再确认标记。",
    discardTitle: "放弃未保存的草稿修改？",
    discardListDescription: "当前候选问题有未保存修改，返回队列会丢弃这些修改。",
    discardNeighborDescription: "当前候选问题有未保存修改，切换到其他候选问题会丢弃这些修改。",
    discardHint: "选择「继续编辑」可先保存草稿再离开。",
    discardConfirm: "放弃修改并离开",
    keepEditing: "继续编辑",
  },
  questionBank: {
    globalDescription: "跨项目检索问题与当前版本；新增、导入和编辑请进入所属项目题库。",
    globalProjectFilterAria: "按所属项目筛选题库",
    globalSummary: "共 {count} 道问题",
    globalFilteredHint: "当前仅显示符合筛选条件的问题。",
    globalNoQuestionsHint: "当前还没有可查看的问题；请在具体项目题库中新增或导入。",
    globalQuestionId: "问题 ID",
    globalCurrentVersion: "当前版本",
    openInProject: "在项目中查看",
    backToAllBanks: "返回全部题库",
    manageProjects: "管理项目",
    pickProjectTitle: "选择一个项目题库",
    pickProjectHint: "无项目上下文时不能直接新建或导入问题。",
    loadingProjects: "正在加载项目",
    projectsLoadFailed: "项目加载失败",
    projectFallbackDescription: "维护该项目的问题与版本",
    questionCount: "{count} 题",
    noProjects: "还没有可用项目",
    noProjectsHint: "请先创建项目，再向项目题库添加问题。",
    pageTitle: "{name} · 题库",
    pageDescription: "勾选题组成卷，或导入 Excel 补料。编辑会生成新版本；历史测试集继续引用原版本。",
    backToSuite: "返回测试集",
    backToContent: "返回测试集",
    tagManager: "标签管理",
    import: "导入",
    newQuestion: "新增问题",
    editQuestion: "编辑问题",
    importStatus: {
      uploaded: "已上传",
      parsing: "解析中",
      review: "待确认",
      awaitingSuiteName: "待命名测试集",
      committing: "写入中",
      completed: "已完成",
      failed: "失败",
      cancelled: "已取消",
    },
    currentVersion: "当前版本",
    historicalVersion: "历史版本",
    multiTurn: "多轮 · {count} 轮",
    multiTurnShort: "多轮·{count}",
    singleTurn: "单轮",
    conversationFilter: "会话",
    conversationFilterAria: "会话类型筛选",
    conversationAll: "全部",
    conversationMulti: "多轮",
    turnsFilter: "轮次",
    turnsFilterAria: "轮次数筛选",
    turnsFilterAll: "全部多轮档位",
    turnBucket: {
      exact: "{count} 轮",
      fivePlus: "5+ 轮",
    },
    activeFiltersAria: "当前生效的筛选条件",
    activeFilter: {
      search: "搜索：{query}",
      status: "状态：{status}",
      conversation: "会话：{mode}",
      turns: "轮次：{turns}",
      tag: "标签：{tag}",
    },
    removeFilter: "移除筛选 {label}",
    moreTags: "+{count}",
    suiteBarHintOutsideFilter: "已选 {count} 道题（其中 {outside} 道不在当前筛选）",
    keepVisibleSelection: "仅保留可见",
    clearSelection: "清空已选",
    versionTurnsAria: "v{version} 对话轮次",
    turnOrdinal: "第 {index} 轮",
    turnId: "Turn ID：{id}",
    userInput: "用户输入",
    groundTruth: "标准答案",
    actionFailedRetry: "操作失败，请稍后重试",
    suiteNameRequired: "请输入测试集名称",
    suiteNameTooLong: "测试集名称不能超过 {max} 个字符",
    suiteNameDuplicate: "当前项目已存在同名测试集",
    selectAtLeastOne: "请先选择至少 1 道题",
    importLoadFailed: "导入记录加载失败：{message}",
    questionDisabled: "问题 {code} 已停用",
    questionEnabled: "问题 {code} 已启用",
    disableFailed: "停用问题失败：{message}",
    enableFailed: "启用问题失败：{message}",
    tagReused: "已使用现有标签",
    tagCreated: "项目标签已创建并选中",
    editorRequiredFields: "请填写问题 ID，以及每一轮的用户输入和标准答案",
    savedAsNewVersion: "问题已保存为新版本",
    questionAdded: "问题已加入当前项目",
    saveFailed: "保存问题失败：{message}",
    historyLoadFailed: "版本历史加载失败：{message}",
    targetProject: "目标项目",
    copied: "已复制到 {name}，后续版本相互独立",
    copyFailed: "复制问题失败：{message}",
    deletedWithSuites: "问题已删除；{count} 个已有测试集仍保留原版本",
    deleted: "问题已从当前项目题库删除",
    deleteFailed: "删除问题失败：{message}",
    suiteCreated: "测试集已创建：{name}。可继续创建评测。",
    suiteCreateFailed: "创建测试集失败：{message}",
    pickXlsx: "请选择 .xlsx 文件",
    parsedWithConflicts: "解析完成，发现 {count} 个待处理冲突",
    parsedReady: "解析完成，可以提交导入",
    parseFailed: "解析导入文件失败：{message}",
    importCommitted: "导入已提交到当前项目",
    suiteNameConflict: "测试集名称冲突，请修改名称后继续",
    importIntentConflict: "该导入已由另一套处理方案先提交，页面已刷新为数据库实际结果",
    commitFailed: "提交导入失败：{message}",
    composeHint: "创建测试集模式：勾选问题后在底部填写测试集名称并创建；测试集将使用当前问题版本。",
    locatedPrefix: "已定位问题",
    locatedSuffix: "，并清除可能遮挡它的筛选条件。",
    closeLocation: "关闭定位",
    questionMissing: "链接中的问题不在当前项目，或已经被删除。",
    closeNotice: "关闭提示",
    tagFilters: "标签筛选",
    recentImports: "最近导入",
    rowCount: "{count} 行",
    noImports: "暂无导入记录",
    newImport: "新建导入",
    searchPlaceholder: "搜索问题 ID 或问题",
    clearSearch: "清空搜索",
    questionStatus: "问题状态",
    questionStatusFilterAria: "问题状态筛选",
    statusActive: "已启用",
    statusDisabled: "已停用",
    statusArchived: "已归档",
    refreshBank: "刷新题库",
    listSummary: "显示 {range} / 共 {total} 道",
    selectedSuffix: " · 已选 {count}",
    listSummaryTail: " · 仅当前页可全选 · 排序：{sort}",
    defaultSort: "更新时间倒序",
    sortLabel: "排序",
    sortAria: "问题列表排序",
    sortContentUpdatedDesc: "最近编辑",
    sortCodeAsc: "问题 ID 升序",
    selectedCount: "已选 {count} 道",
    loadingBank: "正在加载题库",
    bankLoadFailed: "题库加载失败",
    noMatching: "没有符合条件的问题",
    noMatchingHint: "筛选条件会继续保留，可以清除后重试。",
    noQuestions: "当前项目还没有问题",
    noQuestionsHint: "新增问题、导入 Excel，或从其他项目复制。",
    importExcel: "导入 Excel",
    selectPageAria: "选择当前页已启用的全部问题",
    selectPageTitle: "只选择当前页中已启用的问题",
    selectQuestion: "选择 {code}",
    disabledCannotSelect: "问题已停用，不能加入测试集",
    fromProduction: "来自 Production",
    disableQuestionAria: "停用问题 {code}",
    enableQuestionAria: "启用问题 {code}",
    disabling: "停用中…",
    enabling: "启用中…",
    history: "历史",
    copyToProject: "复制到其他项目",
    noOtherProjects: "暂无其他可用项目",
    suiteBarAria: "从已选问题创建测试集",
    suiteBarHint: "已选 {count} 道题，填写名称后创建；测试集将固定引用当前版本。",
    suiteNameLabel: "测试集名称 *",
    suiteNamePlaceholder: "例如：库存回归测试",
    suiteNameValid: "名称有效，可以创建包含 {count} 道题的测试集",
    createSuiteButton: "创建测试集（{count}）",
    importTitle: "导入到当前项目",
    importDescription: "统一模板同时支持单轮和多轮：问题维护标签，对话轮次维护用户输入与标准答案。",
    importProgress: "{phase}，已用时 {elapsed}",
    parsingFile: "正在解析文件",
    writing: "正在写入",
    importMode: "导入方式",
    importModeAria: "导入模式",
    importModeBank: "只导入题库",
    importModeSuite: "导入并创建测试集",
    importSuiteNameAria: "导入测试集名称",
    suiteNameHelp: "不超过 {max} 个字符，项目内唯一。",
    excelFile: "Excel 文件",
    pickExcelAria: "选择 Excel 文件",
    downloadTemplate: "下载 Excel 模板",
    reparse: "重新解析并检查",
    parseStep: "1. 解析并检查",
    unitQuestions: "题",
    unitTurns: "轮",
    unitMultiTurn: "多轮",
    unitNew: "新题",
    unitReused: "复用",
    unitBlocking: "阻断冲突",
    recoveryTitle: "问题已写入，测试集尚未创建",
    recoveryHint: "原名称“{name}”已被占用。请在上方输入一个新的测试集名称后继续；不会重复创建问题。",
    committingTitle: "问题已经写入",
    committingHint: "使用锁定名称“{name}”继续创建测试集，不会重复创建问题。",
    suiteNameRecheck: "请修改上方测试集名称；后端提交前还会再次校验。",
    column: {
      codeVersion: "问题 ID / 版本",
      conversation: "会话",
      row: "行",
      codeQuestion: "问题 ID /问题",
      suggestion: "建议",
      conflict: "冲突",
      resolution: "处理",
    },
    action: {
      create: "新增",
      reuse: "复用",
      updateTags: "更新标签",
      review: "需复核",
    },
    noConflict: "无冲突",
    autoReuse: "自动复用",
    autoUpdateTags: "自动更新标签",
    autoCreate: "自动新增",
    footerCompleted: "导入已经完成。",
    footerNeedNewName: "请输入一个与原名称不同的新名称。",
    footerNameAvailable: "新名称可用，可以继续创建测试集。",
    footerCommitting: "问题已经写入，可以继续创建测试集并完成导入。",
    footerLocked: "当前导入状态不可继续，请刷新记录或联系管理员。",
    footerUnresolved: "还有 {count} 个阻断冲突需要选择处理方式。",
    footerReady: "所有阻断冲突均已处理，可以提交。",
    viewSuite: "查看测试集",
    continueWithNewName: "使用新名称继续",
    continueImport: "继续完成导入",
    commitStep: "3. 确认写入",
    editorDescription: "一轮即单轮，多轮按顺序连续执行；标签作用于整道问题，评估规则在评测运行中配置。",
    saveNewVersion: "保存新版本",
    createQuestion: "创建问题",
    codeHelp: "问题在项目内的唯一编号。",
    codeRequired: "问题 ID 必填。",
    codeRequiredLabel: "问题 ID *",
    moveUp: "上移",
    moveDown: "下移",
    moveUpAria: "上移第 {index} 轮",
    moveDownAria: "下移第 {index} 轮",
    userInputRequired: "用户输入 *",
    userInputHelp: "本轮发送给被测系统的输入。",
    userInputRequiredError: "用户输入必填。",
    groundTruthRequired: "标准答案 *",
    groundTruthHelp: "评分参考的期望答案。",
    groundTruthRequiredError: "标准答案必填。",
    hidePreview: "收起预览",
    showPreview: "对话预览",
    currentSingleTurn: "当前为单轮问题",
    currentMultiTurn: "当前为 {count} 轮对话",
    previewUserTurn: "用户 · 第 {index} 轮",
    notFilled: "（尚未填写）",
    loadingTags: "加载可用标签",
    tagsLoadFailed: "标签加载失败，问题内容会保留。",
    discardTitle: "放弃未保存的修改？",
    discardDescription: "关闭编辑器后，本次填写的问题 ID、对话轮次与标签都会丢失。",
    discardConfirm: "放弃修改",
    historyTitle: "{code} · 版本历史",
    historyDescription: "每次编辑生成一个只读历史版本，测试集引用不会漂移。",
    loadingHistory: "正在加载历史",
    copyTitle: "复制问题到其他项目",
    copyDescription: "只复制当前版本；目标项目会获得独立问题和版本历史。",
    copyQuestion: "复制问题",
    targetProjectRequired: "目标项目 *",
    pleaseSelectProject: "请选择项目",
    targetCodeRequired: "目标问题 ID *",
    targetCodeHelp: "目标项目内的问题编号。",
    targetCodeRequiredError: "目标问题 ID 必填。",
    disableTitle: "停用这道问题？",
    disableDescription: "停用后，之后新发起的评测会跳过该题；已有测试集、问题版本和历史运行不会被删除。已经创建的运行不受影响。",
    disableNote: "成功停用后，这道题也会从当前测试集选择中移除；以后可随时重新启用。",
    disableConfirm: "确认停用",
    deleteTitle: "删除这道问题？",
    deleteDescription: "问题会从当前项目题库隐藏，但版本历史、已有测试集和报告中的引用会继续保留。",
    deleteNote: "删除后不能再用这道题创建新的测试集；已有评测数据不会受到影响。",
    deleteConfirm: "删除问题",
    deleting: "删除中…",
    resolveConflictAria: "处理冲突 {id}",
    resolution: {
      reuse: "复用题库版本",
      createVersion: "创建问题新版本",
      newQuestion: "作为新问题导入",
      skip: "跳过此行",
    },
  },
  reportComparison: {
    back: "返回测试报告", scope: "固定测试范围", multipleSuites: "多个测试集", selectedCount: "已选 {count} 份报告", adjust: "调整报告",
    tabsAria: "报告对比视图", tabs: { overview: "对比概览", questions: "逐题差异", snapshots: "运行快照" },
    viewAria: "图表视图", horizontal: "报告横向", timeline: "时序趋势", loading: "正在读取报告快照…", needTwo: "至少选择 2 份可用报告后才能对比。",
    metrics: { platform: "平台稳定性", platformHint: "每份报告的成功返回率", stability: "跨报告回答一致性", stabilityHint: "同一测试集多份报告之间的回答一致程度", experience: "用户体验", experienceHint: "完整回答 P50", cost: "成本", costHint: "Token / 成功问题", quality: "回答质量", qualityHint: "每份报告有效回答的质量分" },
    higherBetter: "越高越好", lowerBetter: "越低越好", latestChanges: "最近一次问题变化", latestChangesHint: "按时间相邻的最后两份报告", questionDifferences: "重点问题差异", questionDifferencesHint: "所选报告最高分与最低分的差距", viewAll: "查看全部", spread: "分差", noCommonQuestions: "没有可共同对比的有效问题。",
    question: "问题", snapshotField: "快照字段", releaseVersion: "Release version", releaseShort: "Release", agentVersion: "AI Engineer 版本", promptVersion: "提示词版本", agent: "AI Engineer", environment: "环境", repeat: "重复次数", notRecorded: "未记录",
    selectorTitle: "调整对比报告", selectorDescription: "当前范围共 {count} 份可用报告；至少选择 2 份，不设固定上限。", apply: "应用选择", searchPlaceholder: "搜索报告、AI Engineer 或版本", historyAction: "查看历史表现", compareSelected: "对比已选报告（{count}）",
    questionTableHint: "点击问题行查看回答详情 · 当前已展示全部 {count} 份报告 · “分差”为最右一列", questionTableScrollHint: "左右滚动查看更多报告", questionDetail: "问题对比详情", questionDetailDescription: "横向查看该题在 {count} 份报告中的回答；每份报告可独立切换重复回答。", previousQuestion: "上一题", nextQuestion: "下一题", standardAnswer: "标准答案", repeatAnswers: "重复回答", repeatNumber: "回答 {count}", duration: "耗时", judgeFindings: "Judge 结论",
  },
  crossEvaluation: {
    title: "多源横评", description: "在多个已登录 AI 平台执行相同评测问题；每个问题可包含单轮或连续多轮提问。",
    navAria: "多源横评导航", nav: { tasks: "横评任务", sources: "数据源" }, configureSources: "配置数据源", newBatch: "新建横评",
    createTitle: "新建跨平台横评", createHint: "选择一个项目测试集和至少两个网页平台；同一问题的多个轮次保持连续会话，不同问题使用独立会话。",
    batchName: "横评名称", batchNamePlaceholder: "例如：跨平台合同审查能力评测", selectProject: "选择项目", selectSuite: "选择测试集", selectSources: "选择平台", evaluationProfile: "评测方案", selectProfile: "选择评测方案", loadingProfiles: "正在加载评测方案…",
    noActiveSources: "还没有已启用的数据源，请先完成数据源连接与网页平台配置。",
    creating: "正在创建…", start: "开始横评", created: "已创建横评“{name}”", createFailed: "创建横评失败：{message}",
    blockerName: "请填写横评名称。", blockerProject: "请选择项目。", blockerSuite: "请选择测试集。", blockerProfile: "请选择用于真实评分的评测方案。", blockerSources: "至少选择 2 个真实数据源；当前已选 {count} 个。", readySources: "已选择 {count} 个真实数据源，可以开始横评。",
    batchList: "横评任务", batchListHint: "查看真实采集进度、部分失败和报告。", loading: "正在加载横评任务", loadingHint: "正在读取已冻结的任务快照和平台状态。",
    loadFailed: "横评任务加载失败", emptyTitle: "还没有横评任务", emptyHint: "配置数据源后，创建一个包含连续问题的横评批次。",
    filteredEmptyTitle: "当前筛选没有结果", filteredEmptyHint: "调整项目、状态、平台或关键字后重试。", searchPlaceholder: "搜索名称、任务 ID 或测试集 ID", sortLabel: "排序", sortNewest: "创建时间：从新到旧", sortOldest: "创建时间：从旧到新", tableAria: "多源横评任务列表", batch: "横评任务", progress: "执行进度", progressValue: "已完成 {completed} / {total}",
    sourcesTitle: "数据源", sourcesDescription: "配置浏览器研究智能体、目标 AI 平台会话和版本化采集 Prompt。",
    configLoading: "正在加载采集配置", configLoadingHint: "正在读取 Connection、数据源和当前 Prompt。",
    connectionTitle: "浏览器研究智能体", connectionHint: "智能体通过只写 API Key 操作已登录浏览器会话；密钥不会出现在读取接口、Prompt、快照或日志中。",
    connectionName: "连接名称", apiBaseUrl: "API Base URL", apiKey: "API Key", apiKeyHint: "仅保存时提交；保存后界面只显示是否已配置。", apiKeySavedPlaceholder: "留空以保留已保存的密钥", createConnection: "创建连接",
    connectionModalTitle: "创建数据源连接", connectionEditTitle: "编辑数据源连接", connectionModalHint: "连接供所有网页平台数据源复用；API Key 将作为只写密钥保存。", connectionCountOne: "1 个连接", connectionCount: "{count} 个连接", connectionLoadFailed: "连接加载失败",
    connectionCreated: "连接已创建", connectionUpdated: "连接已更新", connectionCreateFailed: "创建连接失败：{message}", connectionUpdateFailed: "更新连接失败：{message}", connectionDeleted: "连接已删除", connectionDeleteFailed: "删除连接失败：{message}", noConnections: "还没有连接", noConnectionsHint: "先创建一个 workspace 级连接，再添加网页数据源。",
    disableConnection: "停用", enableConnection: "启用", connectionDeleteTitle: "删除此连接？", connectionDeleteDescription: "“{name}”将被永久删除。请先删除或重新关联使用它的数据源。",
    secretConfigured: "API Key 已配置", probe: "连通性检查", probing: "检查中…", probeSuccess: "网页采集通道在线", probeWarning: "网页采集通道当前离线", probeFailed: "连通性检查失败：{message}",
    probeStatusReady: "连接可用", probeDetailReady: "服务、API Key 和浏览器插件均已验证。",
    probeStatusExtensionOffline: "浏览器插件未连接", probeDetailExtensionOffline: "服务和 API Key 均正常，请确认浏览器插件已启动并连接。",
    probeStatusServiceUnreachable: "无法访问 Agent 服务", probeDetailServiceUnreachable: "检查服务地址、网络和部署状态后重试。",
    probeStatusApiKeyMissing: "API Key 未配置", probeDetailApiKeyMissing: "配置 API Key 后重新检查连接。",
    probeStatusApiKeyInvalid: "API Key 无效", probeDetailApiKeyInvalid: "更新 API Key 后重新检查连接。",
    probeStatusConfigurationInvalid: "连接配置无效", probeDetailConfigurationInvalid: "检查 Agent API 地址和连接参数后重试。",
    probeStatusServiceResponseInvalid: "服务响应异常", probeDetailServiceResponseInvalid: "服务已响应，但返回内容不符合预期，请检查服务版本和 API 地址。",
    probeStatusServiceError: "服务请求失败", probeDetailServiceError: "服务已响应但请求失败，请查看服务日志后重试。",
    sourceTitle: "AI 平台数据源", sourceHint: "每个数据源定义目标平台入口及供浏览器研究智能体使用的平台专属交互指令。",
    sourceName: "数据源名称", platformCode: "平台标识", platformCodeImmutableHint: "用于关联平台账号凭据，数据源创建后不可修改。", publicUrl: "平台网址", targetUrl: "Agent 访问 URL", selectConnection: "选择数据源连接", createSource: "创建数据源",
    platformLogin: "平台登录账号", platformLoginEnabled: "配置此平台的登录账号", platformLoginOptional: "可选。账号密码由 Browser Research Agent 安全保存，本服务不留存副本。", platformUsername: "用户名", platformPassword: "密码", platformPasswordSaved: "留空以保留已保存的密码", platformPasswordRequired: "新增账号或更换 Agent 连接时必须填写密码。", platformPasswordOptional: "留空将继续使用已保存的密码。",
    sourceModalTitle: "创建 AI 平台数据源", sourceEditTitle: "编辑 AI 平台数据源", sourceModalHint: "一个数据源对应一个已登录平台会话，并通过所选数据源连接执行采集。", sourceCount: "{count} 个数据源", sourceNeedsConnection: "需要先配置数据源连接", sourceNeedsConnectionHint: "网页数据源必须关联一个可用的系统级连接。",
    sourceCreated: "网页数据源已创建", sourceUpdated: "数据源已更新", sourceDeleted: "数据源已删除", sourceCreateFailed: "创建数据源失败：{message}", sourceUpdateFailed: "更新数据源失败：{message}", sourceDeleteFailed: "删除数据源失败：{message}", sourceDeleteTitle: "删除此数据源？", sourceDeleteDescription: "“{name}”及其可复用采集任务将被永久删除。请先删除历史横评记录。", sourceLoadFailed: "数据源加载失败", noSources: "还没有网页数据源", noSourcesHint: "添加已登录的 ChatGPT、Gemini 与 ContractIQ 网页会话入口。", sourceTableAria: "多源横评数据源列表", disableSource: "停用", enableSource: "启用",
    interactionPrompt: "平台交互指令", interactionPromptHint: "描述该平台的导航方式、基于可见语义的定位、会话规则和完成信号；不要在这里重新定义统一 JSON 结果结构。",
    advancedConfiguration: "高级配置", promptTitle: "采集 Prompt", promptHint: "模板负责连续提问、等待完整回答，并要求输出固定版本 JSON；每次开跑都会冻结实际模板。", promptBody: "Prompt 模板",
    promptContractHint: "必须保留 collection_task_id、source_id、platform_code、source_name、platform_instructions、questions_json 六个双花括号变量。JSON v2 会校验采集任务与平台身份、轮次数量、顺序、正文、状态和真实观测时间。",
    promptSaved: "采集 Prompt 已保存", promptSaveFailed: "保存采集 Prompt 失败：{message}", restorePrompt: "恢复默认模板", promptRestored: "已恢复默认采集 Prompt", promptRestoreFailed: "恢复默认 Prompt 失败：{message}", promptLoadFailed: "采集 Prompt 加载失败",
    deleteEvaluationTitle: "删除此横评？", deleteEvaluationDescription: "“{name}”及其全部采集执行、答案、评测结果和事件将被永久删除。", evaluationDeleted: "横评已删除", evaluationDeleteFailed: "删除横评失败：{message}",
    backToList: "返回横评任务", detailLoading: "正在加载横评详情", detailLoadingHint: "正在读取平台执行、答案和状态事件。", detailLoadFailed: "横评详情加载失败",
    profileSnapshot: "本次真实评分使用：{name} · v{version}（已冻结）",
    detailDescription: "项目 {project} · 测试集 {suite}", stop: "停止任务", stopping: "停止中…", stopRequested: "已请求停止横评任务", stopFailed: "停止任务失败：{message}", stopConfirmTitle: "停止本次横评？", stopConfirmDescription: "正在运行的平台采集将被停止，已经采集的答案仍会保留。", confirmStop: "停止横评",
    retryFailedSources: "重试失败平台", retrying: "正在重试…", retryQueued: "失败平台已重新排队", retryFailed: "重试失败：{message}", viewReport: "查看横评报告", partialFailureNotice: "部分平台采集失败；成功平台的真实答案仍可查看和报告，失败平台可单独重试。",
    statusHint: "批次聚合状态", sourcesCompleted: "完成平台", sourcesFailed: "失败平台", platformResults: "平台结果", platformResultsHint: "完成 / 部分完成 / 失败", platformTotal: "所选平台", questionsCaptured: "真实答案", sourceRuns: "平台执行", sourceRunsHint: "每个平台独立运行；平台内每个问题关联一个可复用采集任务，每次执行保留独立记录。",
    collectionQuestion: "问题 {order}", turnCount: "{count} 轮连续提问", executionDetails: "执行详情", previousAttempts: "历史尝试（{count}）", collectionExecutionId: "采集执行 ID", executionAttempt: "执行次数", mispTaskId: "采集任务 ID", queueEntryId: "执行队列 ID", attempt: "Worker 尝试", attemptValue: "{current} / {max}", capturedAnswers: "真实采集答案", capturedAnswersHint: "答案按问题与轮次持久化；原始采集输出同时保留用于重解析。",
    viewRawOutput: "查看原始输出", hideRawOutput: "收起原始输出", rawOutputLoading: "正在按需读取原始采集输出…", rawOutputLoadFailed: "原始输出读取失败：{message}", rawOutputEmpty: "本次执行没有可查看的原始输出。", rawOutputSafetyNotice: "敏感信息掩码：{redacted} · 内容截断：{truncated}",
    answersPending: "正在等待真实答案", answersPendingHint: "浏览器研究智能体正在完成平台会话，页面会自动刷新。", noAnswers: "没有可展示的答案", noAnswersHint: "查看平台错误与状态事件后重试失败来源。",
    assessmentStatus: "评分状态", assessmentPending: "等待评分", assessmentRunning: "评分中", assessmentSkipped: "无可评分答案", assessmentFailed: "评分失败：{message}", assessmentFailedShort: "评分失败",
    answerTableAria: "多源横评真实答案", order: "顺序", question: "问题", platform: "平台", answer: "回答", assessment: "评测", emptyAnswer: "未取得回答", timingUnavailable: "未提供实测耗时", notAssessed: "尚未评测",
    events: "活动记录", eventCount: "{count} 条事件", eventsHint: "按时间记录批次和平台执行的关键状态，不包含 API Key。", eventsLoadFailed: "状态事件加载失败", noEvents: "暂无状态事件", noEventsHint: "任务开始后会在此记录关键状态。",
    backToDetail: "返回横评详情", reportLoading: "正在生成横评报告", reportLoadingHint: "正在组合问题、平台、真实答案与评测结果。", reportLoadFailed: "横评报告加载失败",
    reportTitle: "{name} · 横评报告", reportDescription: "按问题横向查看各网页平台的真实回答、实测耗时和评测结果。", downloadJson: "下载 JSON", downloadMarkdown: "下载报告", printPdf: "打印 / 保存 PDF",
    horizontalComparison: "逐题横向对比", horizontalComparisonHint: "同一道问题下并列展示各平台回答；问题内的连续轮次保留上下文，不同问题彼此隔离。", reportEmpty: "报告尚无答案", reportEmptyHint: "等待至少一个平台采集成功后再查看。",
    coverage: "答案覆盖率", coverageHint: "已采集 {captured} / 应有 {expected}", sourceSuccessFailure: "平台成功 / 失败", successFailureHint: "按真实平台执行结果统计", averageDuration: "平均实测耗时", measuredCount: "基于 {count} 条带浏览器实测时间的回答", averageAnswerLength: "平均回答长度", characterUnit: "字符数，仅统计真实采集回答", averageScore: "平均质量评分", scoredAnswerCount: "基于 {count} 条已评分回答", reportMatrixAria: "问题与平台回答矩阵",
    questionOrder: "问题 {order}", groundTruth: "标准答案：{answer}", sourceNoAnswer: "该平台未取得此题回答。", viewFullAnswer: "查看完整回答", answerDetailTitle: "回答详情", answerDetailDescription: "问题 {order} · {source}", answerDetailNavigation: "回答详情翻页", previousQuestion: "上一题", nextQuestion: "下一题", answerLength: "回答长度", answerLengthValue: "{count} 个字符", noAnswerDetailHint: "可切换上一题或下一题，检查该平台其他采集结果。",
  },
} satisfies MessageTree;

export type AppMessages = typeof zhCN;
